import { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileTasksPage from './MobileTasksPage';
import { ElevateInfluenceCard } from '../components/ElevateInfluenceCard';
import CreateElevateTaskModal from '../components/CreateElevateTaskModal';
import ElevatePendingBanner from '../components/ElevatePendingBanner';
import { taskDisplayTitle } from '../lib/utils';
import { Avatar } from '../components/Logo';
import PlanningPage from './PlanningPage'; // Iter47t — Calendar view embedded inside Tâches
import AdminTodoView from './AdminTodoView';
import AdminTodoTaskDialog from './AdminTodoTaskDialog';
import VideoPhaseWorkflow from '../components/VideoPhaseWorkflow';
import TasksBilanView from '../components/TasksBilanView';
import { STAGES, STAGE_ORDER, PRIORITIES, formatRelative, CATEGORY_TAGS, CATEGORY_TAG_ORDER, FOUNDER_TAG_ORDER, AXEL_HIDDEN_TAGS } from '../constants/users';
import { TagPill, TimeSlot } from '../components/TagPill';
import { FrameioReviewSection, FrameioToggle } from '../components/FrameioReviewSection';
import { QuickDeliverySection } from '../components/QuickDeliverySection';
import { localToday, parseLocalDate, formatDay } from '../lib/dates';
import {
  Plus, Search, X, Trash2, Clock, ChevronDown, Loader2, ArrowLeft, Archive, Check, AlertTriangle, Repeat,
  ProjectIcon, ChevronRight, ChevronLeft, LayoutGrid, Calendar as CalendarIcon, List,
  LinkIcon, Folder, Lock, Sparkles, ArrowUpRight, Pencil, GripVertical, Zap, SettingsIcon as Settings, MoreVertical,
  Eye, EyeOff,
} from '../components/icons';

// Iter47t — Toggle Liste / Calendrier at the very top of the Tasks page.

// Iter62aa — Founder-only popover to set the default Tasks scope on connection.
// Persisted per-founder in localStorage['asa_tasks_default_scope'] = {scope, userId}.
function DefaultScopePopover({ users, scope, userId, onSave, onClose }) {
  const [enabled, setEnabled] = useState(scope === 'custom' && !!userId);
  const [pickedUser, setPickedUser] = useState(userId || '');
  const nonFounderUsers = (users || []).filter((u) => u.role !== 'founder');

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 300 }} />
      <div
        data-testid="tasks-default-scope-popover"
        style={{
          position: 'absolute', top: 44, left: 0, zIndex: 301,
          width: 320, padding: 16, borderRadius: 16, overflow: 'hidden',
          background:
            'radial-gradient(120% 100% at 0% 0%, rgba(245,166,35,0.14) 0%, rgba(245,166,35,0) 55%),' +
            ' linear-gradient(160deg, rgba(38,48,90,0.95) 0%, rgba(18,26,58,0.98) 100%)',
          border: '1.5px solid rgba(245,166,35,0.28)',
          boxShadow: '0 28px 56px -18px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: 'var(--t1)', marginBottom: 6, letterSpacing: '-0.01em' }}>
          Vue par défaut
        </div>
        <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginBottom: 12, lineHeight: 1.45 }}>
          À la connexion, tu verras d&apos;abord les tâches de l&apos;employé choisi (au lieu de tes propres tâches).
        </div>

        <label style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10,
          background: enabled ? 'linear-gradient(135deg, rgba(245,166,35,0.14), rgba(245,166,35,0.04))' : 'rgba(255,255,255,0.03)',
          border: `1.5px solid ${enabled ? 'rgba(245,166,35,0.35)' : 'rgba(255,255,255,0.06)'}`,
          cursor: 'pointer', marginBottom: 10,
        }}>
          <input
            type="checkbox"
            data-testid="default-scope-toggle"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            style={{ accentColor: '#F5A623' }}
          />
          <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: 'var(--t1)' }}>
            Voir les tâches d&apos;un employé par défaut
          </span>
        </label>

        {enabled && (
          <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
            {nonFounderUsers.map((u) => {
              const on = pickedUser === u.id;
              return (
                <button
                  key={u.id}
                  data-testid={`default-scope-user-${u.id}`}
                  onClick={() => setPickedUser(u.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 10px', borderRadius: 10,
                    background: on ? `linear-gradient(135deg, ${u.color || '#5BBEFC'}22, ${u.color || '#5BBEFC'}0A)` : 'transparent',
                    border: `1px solid ${on ? `${u.color || '#5BBEFC'}55` : 'rgba(255,255,255,0.06)'}`,
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: `${u.color || '#5BBEFC'}22`,
                    border: `1.5px solid ${u.color || '#5BBEFC'}66`,
                    color: u.color || '#5BBEFC',
                    display: 'grid', placeItems: 'center',
                    fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
                  }}>{(u.name || '?').charAt(0)}</div>
                  <div style={{ flex: 1, fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: 'var(--t1)' }}>{u.name}</div>
                  {on && <Check size={13} color={u.color || '#5BBEFC'} strokeWidth={2.6} />}
                </button>
              );
            })}
          </div>
        )}

        <button
          data-testid="default-scope-save"
          onClick={() => onSave(enabled ? 'custom' : 'mine', enabled ? pickedUser : '')}
          disabled={enabled && !pickedUser}
          style={{
            width: '100%', padding: 11, borderRadius: 12,
            background: 'linear-gradient(135deg, rgba(245,166,35,0.28), rgba(245,166,35,0.10))',
            border: '1.5px solid rgba(245,166,35,0.55)',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5, color: '#F5D08A',
            cursor: (enabled && !pickedUser) ? 'not-allowed' : 'pointer',
            opacity: (enabled && !pickedUser) ? 0.55 : 1,
            boxShadow: '0 8px 20px -6px rgba(245,166,35,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
          }}
        >Enregistrer</button>
      </div>
    </>
  );
}


// Rayan asked to remove the separate "Calendrier" sidebar entry — we merge both here.
function TasksViewToggle({ mode, onChange }) {
  const OPTS = [
    { key: 'list',     label: 'Liste' },
    { key: 'calendar', label: 'Calendrier' },
    { key: 'bilan',    label: 'Bilan' },
  ];
  return (
    <div style={{ padding: '18px 22px 0 22px' }}>
      <div style={{ display: 'inline-flex', gap: 4, padding: 4, borderRadius: 10, background: 'var(--sf)', border: '1.5px solid var(--bd)' }}>
        {OPTS.map((o) => {
          const active = mode === o.key;
          return (
            <button
              key={o.key}
              type="button"
              data-testid={`tasks-view-${o.key}`}
              onClick={() => onChange(o.key)}
              style={{
                padding: '6px 16px', borderRadius: 7,
                background: active ? 'rgba(91,190,252,0.14)' : 'transparent',
                border: `1px solid ${active ? 'rgba(91,190,252,0.45)' : 'transparent'}`,
                color: active ? '#5BBEFC' : 'var(--t3)',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                cursor: 'pointer', transition: 'all 0.12s',
              }}
            >{o.label}</button>
          );
        })}
      </div>
    </div>
  );
}


const FILTERS = [
  { id: 'all',         label: 'Tous',       color: '#8A9DB8' },
  { id: 'todo',        label: 'À faire',    color: '#C8D6E8' },
  { id: 'in_progress', label: 'En cours',   color: '#2EA3F2' },
  { id: 'review',      label: 'À valider',  color: '#F5A623' },
  { id: 'done',        label: 'Terminé 24h',color: '#34D399' },
];

const SCOPES = [
  { id: 'all',      label: 'Toutes',     icon: 'all' },
  { id: 'mine',     label: 'Admin',      icon: 'mine' },
  { id: 'team',     label: 'Équipe',     icon: 'team' },
  { id: 'client',   label: 'Par client', icon: 'client' },
  { id: 'custom',   label: 'Personnalisée', icon: 'custom' },
];

// Iter 62cw v2 (Rayan msg 2026-02-20) — Team switcher épuré Apple.
// Affiche 3 pills principaux : Moi (Rayan), Axel, Elevate. Un bouton "..." dévoile
// les autres membres dans un menu. Click sur un membre → ancien design POV.
// Click sur "Elevate" → filtre à scope=custom pour aryan (Elevate Influence).
function TeamSwitcher({ users, currentScope, currentCustomUserId, currentUserId, onSelectMine, onSelectUser }) {
  const [showMore, setShowMore] = useState(false);
  const list = (users || []).filter((u) => (u.role === 'contributor' || u.is_monteur) && !u.is_archived);
  // Iter 62cw v2 — 3 pills fixes en priorité
  const pinnedIds = ['axel', 'aryan'];  // aryan = Elevate Influence
  const pinned = pinnedIds.map((id) => list.find((u) => u.id === id)).filter(Boolean);
  const others = list.filter((u) => !pinnedIds.includes(u.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const isMineActive = currentScope === 'mine';
  const displayNameForPill = (u) => u.id === 'aryan' ? 'Elevate' : (u.name || '').split(' ')[0];
  const activeOther = currentScope === 'custom' && currentCustomUserId && !pinnedIds.includes(currentCustomUserId)
    ? list.find((u) => u.id === currentCustomUserId)
    : null;

  return (
    <div
      data-testid="tasks-team-switcher"
      style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 22,
      }}
    >
      <button
        data-testid="team-switcher-mine"
        onClick={onSelectMine}
        style={pillStyle(isMineActive, '#5BBEFC')}
      >
        <Dot color="#5BBEFC" /> Admin
      </button>
      {pinned.map((u) => {
        const active = currentScope === 'custom' && currentCustomUserId === u.id;
        const color = u.id === 'aryan' ? '#F5C56A' : (u.color || '#5BBEFC');
        return (
          <button
            key={u.id}
            data-testid={`team-switcher-${u.id}`}
            onClick={() => onSelectUser(u.id)}
            style={pillStyle(active, color)}
          >
            <Dot color={color} />
            {displayNameForPill(u)}
          </button>
        );
      })}
      {activeOther && (
        <button
          data-testid={`team-switcher-${activeOther.id}`}
          onClick={() => onSelectUser(activeOther.id)}
          style={pillStyle(true, activeOther.color || '#5BBEFC')}
        >
          <Dot color={activeOther.color || '#5BBEFC'} />
          {activeOther.name.split(' ')[0]}
        </button>
      )}
      <div style={{ position: 'relative' }}>
        <button
          data-testid="team-switcher-more"
          onClick={() => setShowMore((v) => !v)}
          aria-label="Voir les autres membres"
          style={{
            width: 36, height: 32, borderRadius: 999,
            display: 'grid', placeItems: 'center',
            background: 'var(--sf)', border: '1px solid var(--bd)',
            color: 'var(--t2)', cursor: 'pointer',
            fontSize: 15, fontWeight: 800, letterSpacing: '0.1em',
          }}
        >⋯</button>
        {showMore && others.length > 0 && (
          <>
            <div onClick={() => setShowMore(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
            <div
              data-testid="team-switcher-more-menu"
              style={{
                position: 'absolute', top: 40, left: 0,
                zIndex: 50, minWidth: 220,
                background: 'var(--bg)', borderRadius: 12,
                border: '1px solid var(--bd)',
                boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
                padding: 6,
              }}
            >
              {others.map((u) => (
                <button
                  key={u.id}
                  data-testid={`team-switcher-more-${u.id}`}
                  onClick={() => { onSelectUser(u.id); setShowMore(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    width: '100%', padding: '8px 10px', borderRadius: 8,
                    background: 'transparent', border: 'none', color: 'var(--t2)',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                    cursor: 'pointer', textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sf)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <Dot color={u.color || '#5BBEFC'} />
                  {u.name}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
function pillStyle(active, color) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '7px 14px', borderRadius: 999,
    background: active ? `linear-gradient(140deg, ${color}22, ${color}08)` : 'var(--sf)',
    border: `1px solid ${active ? color : 'var(--bd)'}`,
    color: active ? color : 'var(--t2)',
    fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
    cursor: 'pointer',
    transition: 'all 0.15s var(--ease-out)',
    letterSpacing: '-0.005em',
  };
}
function Dot({ color }) {
  return <span style={{ width: 6, height: 6, borderRadius: 999, background: color, flexShrink: 0 }} />;
}

export default function TasksPage() {
  // Iter61e — dedicated mobile Tâches (user-provided HTML redesign v3)
  const isMobile = useIsMobile();
  if (isMobile) return <MobileTasksPage />;
  return <TasksPageDesktop />;
}

function TasksPageDesktop() {
  const { user, isFounder, isFounderStrict } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // Iter47l — DEFAULTS toujours "Privée + Aujourd'hui" (Rayan: "jamais Toutes")
  // Iter62aa — Founders can override default scope to see a specific employee's tasks
  const [scope, setScope] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('asa_tasks_default_scope') || 'null');
      if (s && s.scope) return s.scope;
    } catch { /* ignore */ }
    return 'mine';
  });
  const [customUserId, setCustomUserId] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('asa_tasks_default_scope') || 'null');
      if (s && s.scope === 'custom' && s.userId) return s.userId;
    } catch { /* ignore */ }
    return '';
  }); // Iter33: 'Personnalisée' scope picker
  const [showDefaultScope, setShowDefaultScope] = useState(false); // Iter62aa — Founder-only settings popover
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateElevate, setShowCreateElevate] = useState(false);
  const [showFromDoc, setShowFromDoc] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editingTask, setEditingTask] = useState(null);  // Iter43h — Quick edit modal
  const [layout, setLayout] = useState('list');  // 'list' | 'calendar' | 'bilan'
  const [calCursor, setCalCursor] = useState(new Date());
  // Iter47i — Time view filter: 'day' (aujourd'hui) | 'week' | 'month' | 'all'
  // Iter61t — Default to 'week' (not 'day') so contributors see their upcoming tasks
  // and not just today's — user reported Axel didn't see all his tasks in List view.
  const [timeView, setTimeView] = useState(() => localStorage.getItem('asa_time_view') || 'week');
  useEffect(() => { localStorage.setItem('asa_time_view', timeView); }, [timeView]);
  const [categoryFilter, setCategoryFilter] = useState(null); // Iter47x — filter by category tag
  // Iter47l — Founder-only toggle: show project cards in the tasks list (default off, monteurs never see projects)
  const [showProjects, setShowProjects] = useState(() => localStorage.getItem('asa_tasks_show_projects') === '1');
  const [showViewSettings, setShowViewSettings] = useState(false);
  useEffect(() => { localStorage.setItem('asa_tasks_show_projects', showProjects ? '1' : '0'); }, [showProjects]);
  // Iter 62cw — Toggle "afficher Elevate School dans les tâches" (founder strict).
  // Prefs par user en localStorage, default = true.
  const [showElevateInTasks, setShowElevateInTasks] = useState(() => localStorage.getItem('asa_tasks_show_elevate') !== '0');
  useEffect(() => { localStorage.setItem('asa_tasks_show_elevate', showElevateInTasks ? '1' : '0'); }, [showElevateInTasks]);

  // Iter 62cx (Rayan msg 2026-02-20) — Admin scope has two modes:
  //   - 'todo' : nouvelle Admin To-Do List premium (Rayan + Hugo, modal dédié)
  //   - 'team' : ancien design équipe/kanban (fall-through vers la vue habituelle)
  // Persisté par utilisateur dans localStorage. Default = 'todo'.
  const [adminMode, setAdminMode] = useState(() => localStorage.getItem('asa_admin_mode') || 'todo');
  useEffect(() => { localStorage.setItem('asa_admin_mode', adminMode); }, [adminMode]);
  const [showAdminCreate, setShowAdminCreate] = useState(false); // Nouveau modal premium en mode To-Do List
  const [adminEditTask, setAdminEditTask] = useState(null); // Iter 62cz — Modal d'édition d'une tâche admin_todo

  // Iter47x — reload when a task is deleted via the 3-dots menu
  useEffect(() => {
    const handler = () => reload();
    window.addEventListener('asa-task-updated', handler);
    return () => window.removeEventListener('asa-task-updated', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = async () => {
    const [t, u, p] = await Promise.all([
      api.get('/tasks'),
      api.get('/users'),
      api.get('/projects'),
    ]);
    // Iter34 — enrich tasks with display_title (suffix project client/name)
    const projMap = {};
    (p.data || []).forEach((pr) => { projMap[pr.id] = pr; });
    const enriched = (t.data || []).map((tk) => ({
      ...tk,
      display_title: taskDisplayTitle(tk, projMap),
    }));
    setTasks(enriched); setUsers(u.data); setProjects(p.data); setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  // Iter43 — deep-link : /tasks?open={task_id} auto-opens the drawer for that task
  // Iter61w — also accept legacy `?task={id}` param (older notifications may still contain it)
  useEffect(() => {
    const openId = searchParams.get('open') || searchParams.get('task');
    const focus = searchParams.get('focus');
    if (!openId || !tasks.length) return;
    const t = tasks.find((x) => x.id === openId);
    if (t) {
      setSelected(t);
      // Iter62y — Auto-scroll to Frame.io section when the notification asks for it
      if (focus === 'frame') {
        setTimeout(() => {
          const el = document.getElementById('frameio-focus-anchor');
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.style.transition = 'box-shadow 0.4s ease';
            el.style.boxShadow = '0 0 0 3px rgba(245,166,35,0.55)';
            setTimeout(() => { el.style.boxShadow = 'none'; }, 1800);
          }
        }, 380);
      }
      // strip the query param so refresh/back doesn't re-open
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      next.delete('task');
      next.delete('focus');
      setSearchParams(next, { replace: true });
    }
  }, [tasks, searchParams, setSearchParams]);

  // Iter43 — counts per stage retirés (anti-anxiété : plus d'affichage du nombre de tâches)

  const filtered = useMemo(() => {
    // Hide sub-tasks from main list — they're shown inside the project detail
    let list = tasks.filter((t) => !t.parent_task_id);
    // Iter 62cz — Isolation des tâches admin_todo :
    //   - En mode Admin To-Do List (adminMode==='todo' + scope==='mine' + founder) : garder UNIQUEMENT les admin_todo
    //   - Dans TOUTES les autres vues : exclure les admin_todo (jamais visibles pour monteurs, planning, kanban, mobile…).
    {
      const inAdminTodoMode = isFounderStrict && scope === 'mine' && adminMode === 'todo';
      if (inAdminTodoMode) {
        list = list.filter((t) => t.source === 'admin_todo');
      } else {
        list = list.filter((t) => t.source !== 'admin_todo');
      }
    }
    // Iter 62cu — Toggle "Masquer pour aujourd'hui" (Rayan msg 2026-02-20) : les
    // tâches avec hidden_on === date du jour disparaissent du Hub Tâches (elles
    // restent partout ailleurs : planning, projet, calendrier). Reset naturel à
    // minuit puisque "today" change.
    {
      const now = new Date();
      const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      list = list.filter((t) => t.hidden_on !== todayISO);
    }
    // scope filter
    if (scope === 'mine') {
      // Iter 62cx — En mode Admin To-Do List (founder strict + adminMode==='todo'),
      // on affiche les tâches de Rayan ET Hugo pour alimenter les 2 colonnes.
      if (isFounderStrict && adminMode === 'todo') {
        list = list.filter((t) => {
          const owners = ['rayan', 'hugo'];
          const isOwned = owners.includes(t.assigned_to)
            || owners.includes(t.assigned_to_derush)
            || owners.includes(t.assigned_to_montage)
            || (Array.isArray(t.assignees) && t.assignees.some((a) => owners.includes(a)));
          if (isOwned) return true;
          const hasAnyAssignee = !!(t.assigned_to || t.assigned_to_derush || t.assigned_to_montage || (t.assignees && t.assignees.length));
          return !hasAnyAssignee && owners.includes(t.created_by);
        });
      } else {
      // Iter42g — Fix : match ANY assignment type (assigned_to / assignees /
      // assigned_to_derush / assigned_to_montage). Previously we filtered only
      // by `assigned_to`, so a monteur (Axel) with a video task tagged only via
      // `assigned_to_montage` or a multi-assignee via `assignees` didn't see it.
      // Iter 62bu — Also include `created_by === user.id` BUT only when the task
      // has NO explicit assignee (else a founder assigning 20 tasks to the team
      // would clog their own Privée view). Aligns PC ↔ Mobile behaviour.
      list = list.filter((t) => {
        const isAssignee = t.assigned_to === user.id
          || t.assigned_to_derush === user.id
          || t.assigned_to_montage === user.id
          || (Array.isArray(t.assignees) && t.assignees.includes(user.id));
        if (isAssignee) return true;
        const hasAnyAssignee = !!(t.assigned_to || t.assigned_to_derush || t.assigned_to_montage || (t.assignees && t.assignees.length));
        return !hasAnyAssignee && t.created_by === user.id;
      });
      }
    }
    if (scope === 'team') {
      // Team view: tasks where at least someone is assigned
      list = list.filter((t) =>
        t.assigned_to
        || t.assigned_to_derush
        || t.assigned_to_montage
        || (Array.isArray(t.assignees) && t.assignees.length > 0),
      );
    }
    if (scope === 'custom' && customUserId) {
      // Iter33: founder picks any user, see their assigned tasks
      list = list.filter((t) =>
        t.assigned_to === customUserId
        || t.assigned_to_derush === customUserId
        || t.assigned_to_montage === customUserId
        || (Array.isArray(t.assignees) && t.assignees.includes(customUserId)),
      );
    }
    // 'client' scope keeps all tasks but renders them grouped by client (see grouped below)
    if (filter !== 'all') list = list.filter((t) => t.stage === filter);
    // Iter47i — Time view filter: 'day' | 'week' | 'month' | 'all'
    if (timeView !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      // Iter47z — Format in LOCAL timezone (Paris). toISOString() would convert to UTC
      // and shift the date by -1 day for late evening slots → tasks du 11 juillet
      // n'apparaîtraient pas en vue "Aujourd'hui" côté matin/soir. Fixed.
      const iso = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const todayISO = iso(today);
      let horizon;
      if (timeView === 'day') {
        horizon = todayISO;
      } else if (timeView === 'week') {
        const end = new Date(today); end.setDate(end.getDate() + 7);
        horizon = iso(end);
      } else if (timeView === 'month') {
        const end = new Date(today); end.setMonth(end.getMonth() + 1);
        horizon = iso(end);
      }
      list = list.filter((t) => {
        // Always keep group parents (their date is virtual) + not-yet-done tasks without due_date
        if (t.is_group_parent || t.kind === 'group') return true;
        if (!t.due_date) return timeView === 'month' || timeView === 'week';
        // Iter47s — Normalize due_date to YYYY-MM-DD before string comparison.
        // Backend stores some as ISO datetime "YYYY-MM-DDTHH:MM:SS" — without normalization
        // "2026-07-10T17:00" > "2026-07-10", so tasks due today at 17h are filtered out. Bug fixed.
        return t.due_date.slice(0, 10) <= horizon;
      });
    }
    const q = search.toLowerCase();
    if (q) list = list.filter((t) =>
      t.title.toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
    // Iter47x — Category tag filter (all users)
    if (categoryFilter) {
      list = list.filter((t) => t.category_tag === categoryFilter);
    }
    // Iter47x — Axel (chef-monteur) never sees "developpement" tag
    if (user?.id === 'axel') {
      list = list.filter((t) => !AXEL_HIDDEN_TAGS.includes(t.category_tag));
    }
    return [...list].sort((a, b) => {
      // Iter47d — Manual order (founder drag-and-drop) has TOP priority when set.
      const ma = a.manual_order;
      const mb = b.manual_order;
      const hasA = typeof ma === 'number';
      const hasB = typeof mb === 'number';
      if (hasA && hasB) return ma - mb;
      if (hasA) return -1;
      if (hasB) return 1;
      // Iter47x — When BOTH tasks have a start_time, sort chronologically (Axel & everyone).
      // This handles the "ma journée triée par heure" requirement without hurting the rest.
      if (a.start_time && b.start_time && a.start_time !== b.start_time) {
        // Only apply when both have same due_date (or both no due_date)
        const dA = (a.due_date || '').slice(0, 10);
        const dB = (b.due_date || '').slice(0, 10);
        if (dA === dB) return a.start_time.localeCompare(b.start_time);
      }
      // Iter47d.1 — Then: non-done VIDEO tasks pinned at the top (they're the priority of the day).
      const va = a.kind === 'video' && a.stage !== 'done' ? 0 : 1;
      const vb = b.kind === 'video' && b.stage !== 'done' ? 0 : 1;
      if (va !== vb) return va - vb;
      // Iter43f — Priority-first sort: high on top, medium middle, low at bottom
      const prio = { high: 0, medium: 1, low: 2 };
      const pa = prio[a.priority] ?? 1;
      const pb = prio[b.priority] ?? 1;
      if (pa !== pb) return pa - pb;
      const order = { todo: 0, in_progress: 1, review: 2, done: 3 };
      if (order[a.stage] !== order[b.stage]) return order[a.stage] - order[b.stage];
      return (a.due_date || '9999').localeCompare(b.due_date || '9999');
    });
  }, [tasks, filter, search, scope, customUserId, user.id, timeView, categoryFilter, isFounderStrict, adminMode]);

  // Detect video / deliverable tasks (4-block tasks) — used to hide them from Tasks list
  // because they should only appear inside the project view.
  const isVideoDeliverable = (t) => {
    const tags = Array.isArray(t.tags) ? t.tags : [];
    return (
      t.kind === 'video' ||
      tags.includes('video_deliverable') ||
      t.event_type === 'content' ||
      !!t.concept_id ||
      // is_macro tasks from from-doc are video concepts → hide them too
      t.is_macro === true
    );
  };

  // Group: project cards + orphan tasks (hide video deliverables — they live in the project view)
  const grouped = useMemo(() => {
    const orphans = [];
    const groups = [];      // Iter47h — group parents (kind='group')
    const stepsByGroup = new Map(); // gid -> step[]
    const byProject = new Map(); // pid -> { project, tasks }

    // First pass: bucket every task loaded (not just `filtered`), so steps are
    // available even after user applies scope/filter/search on the group parent.
    tasks.forEach((t) => {
      if (t.kind === 'group_step' && t.group_id) {
        if (!stepsByGroup.has(t.group_id)) stepsByGroup.set(t.group_id, []);
        stepsByGroup.get(t.group_id).push(t);
      }
    });

    // Iter 62cc — Pre-seed byProject with ALL non-archived, non-100% projects
    // that have at least one task (even done/archived) so the card appears in
    // the List even when the current scope/filter yields zero matching tasks.
    // Fixes: "un projet ne s'affiche pas en Liste" when tasks are assigned to
    // someone else or completed / out of the active scope.
    const projectsWithTasks = new Set();
    tasks.forEach((t) => { if (t.project_id) projectsWithTasks.add(t.project_id); });
    projects.forEach((p) => {
      if (p.archived) return;
      if (p.progress && p.progress.total > 0 && p.progress.ratio >= 1) return;
      if (projectsWithTasks.has(p.id)) {
        byProject.set(p.id, { project: p, tasks: [] });
      }
    });

    filtered.forEach((t) => {
      if (t.is_group_parent || t.kind === 'group') {
        groups.push(t);
        return;
      }
      if (t.kind === 'group_step') {
        // Steps are rendered inside their group card, not at top level
        return;
      }
      if (t.project_id) {
        const p = projects.find((x) => x.id === t.project_id);
        if (!p) {
          // Iter 62cd — Project not visible/found (archived, deleted, permission).
          // Previously we hid video deliverables entirely — this caused missing
          // videos in the list vs calendar. Now we surface EVERY task as orphan
          // so nothing silently disappears from Aujourd'hui / Liste views.
          orphans.push(t);
          return;
        }
        // Hide projects that are 100% complete
        if (p.progress && p.progress.total > 0 && p.progress.ratio >= 1) return;
        if (!byProject.has(p.id)) byProject.set(p.id, { project: p, tasks: [] });
        byProject.get(p.id).tasks.push(t);
      } else {
        // Orphan task: only show classic tasks (meetings, todos, calls) — never video tasks
        if (!isVideoDeliverable(t)) orphans.push(t);
      }
    });
    return { projects: Array.from(byProject.values()), orphans, groups, stepsByGroup };
  }, [tasks, filtered, projects]);

  // For "Par client" view — re-bucket project groups by client
  const byClient = useMemo(() => {
    if (scope !== 'client') return null;
    const map = new Map(); // client name -> { color, projects: [] }
    grouped.projects.forEach((pg) => {
      const name = pg.project.client || 'Sans client';
      const color = pg.project.client_color || pg.project.color || '#8A9DB8';
      if (!map.has(name)) map.set(name, { name, color, projects: [], tasks: [], groups: [] });
      map.get(name).projects.push(pg);
    });
    grouped.groups.forEach((g) => {
      const name = g.client_name || 'Sans client';
      const color = g.group_color || '#8A9DB8';
      if (!map.has(name)) map.set(name, { name, color, projects: [], tasks: [], groups: [] });
      map.get(name).groups.push(g);
    });
    grouped.orphans.forEach((t) => {
      const name = 'Sans client';
      if (!map.has(name)) map.set(name, { name, color: '#8A9DB8', projects: [], tasks: [], groups: [] });
      map.get(name).tasks.push(t);
    });
    // Sorted: real clients first, "Sans client" at the end
    return Array.from(map.values()).sort((a, b) => {
      if (a.name === 'Sans client') return 1;
      if (b.name === 'Sans client') return -1;
      return a.name.localeCompare(b.name);
    });
  }, [scope, grouped]);

  const openProject = (pid) => {
    localStorage.setItem('asa_open_project', pid);
    nav('/projects');
  };

  // Iter 62cf — Fix Axel/monteurs : afficher TOUTES leurs vidéos assignées.
  // Founders : 2 vidéos globales max (surveillance rapide).
  // Monteurs : TOUTES leurs vidéos (pas de slice, pas de dedup) — sinon les vidéos
  // ratées par `dedupByProject` ou le cap slice(0,2) tombent dans les cartes projets,
  // qui sont MASQUÉES pour les non-founders (`isFounder && showProjects`) → invisibles.
  const todayVideos = useMemo(() => {
    const videoFilter = (t) =>
      t.kind === 'video'
      && !t.posted_online
      && t.stage !== 'done';
    // Rayan: "toujours afficher UNE seule vidéo par projet" — appliqué aux founders uniquement.
    const dedupByProject = (arr) => {
      const seen = new Set();
      const out = [];
      for (const t of arr) {
        const key = t.project_id || `_task_${t.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(t);
      }
      return out;
    };
    if (isFounder) {
      // Iter 62ck — plus de cap 2 pour founders : Hugo/Rayan doivent voir TOUTES
      // les vidéos prioritaires (1 par projet, tous les projets actifs) comme
      // les monteurs, pour piloter la charge globale de l'équipe.
      return dedupByProject(
        tasks
          .filter(videoFilter)
          .sort((a, b) => (a.due_date || '9999-12-31').localeCompare(b.due_date || '9999-12-31'))
      );
    }
    // Monteur / contributor / client : Iter 62cg — 1 vidéo la plus urgente par projet
    // assigné. Ainsi si Axel a 6 projets actifs → 6 vidéos affichées aujourd'hui,
    // 12 projets → 12 vidéos, etc. Pas de cap slice.
    return dedupByProject(
      tasks
        .filter((t) =>
          videoFilter(t)
          && (
            t.assigned_to === user?.id
            || t.assigned_to_derush === user?.id
            || t.assigned_to_montage === user?.id
            || (Array.isArray(t.assignees) && t.assignees.includes(user?.id))
          )
        )
        .sort((a, b) => (a.due_date || '9999-12-31').localeCompare(b.due_date || '9999-12-31'))
    );
  }, [tasks, user?.id, isFounder]);
  // Iter47z — todayStr in LOCAL timezone (Paris) — avoid UTC shift
  const todayStr43 = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  // Iter47t/62t — Merge "Calendrier" and "Bilan" tabs into Tasks with a top toggle.
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('asa_tasks_view') || 'list');
  useEffect(() => { localStorage.setItem('asa_tasks_view', viewMode); }, [viewMode]);
  // Iter62t — Sync top viewMode with the Format dropdown (`layout` state).
  useEffect(() => { setLayout(viewMode); }, [viewMode]);

  if (viewMode === 'calendar') {
    return (
      <div className="page-fade">
        <TasksViewToggle mode={viewMode} onChange={setViewMode} />
        <PlanningPage />
      </div>
    );
  }

  if (viewMode === 'bilan') {
    return (
      <div className="page-fade">
        <TasksViewToggle mode={viewMode} onChange={setViewMode} />
        <div style={{ padding: '18px 22px 60px' }}>
          <TasksBilanView />
        </div>
      </div>
    );
  }

  return (
    <div className="page-fade">
      <TasksViewToggle mode={viewMode} onChange={setViewMode} />
      {/* Header — title + gear on the same line (Iter47j) */}
      <div style={{ padding: '24px 22px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <h1 className="asa-display" style={{ fontSize: 'var(--text-2xl)', margin: 0, flex: 1 }}>Tâches</h1>
          {isFounder && (
            <button
              data-testid="task-archives"
              onClick={() => nav('/archives')}
              title="Archives"
              style={{
                height: 30, padding: '0 12px', borderRadius: 8,
                background: 'transparent', border: '1px solid var(--bd)',
                color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                cursor: 'pointer',
              }}
            >Archives</button>
          )}
          {isFounder && (
            <div style={{ position: 'relative' }}>
              <button
                data-testid="task-view-settings"
                onClick={() => setShowViewSettings((v) => !v)}
                title="Options d'affichage"
                style={{
                  background: 'transparent', border: 'none',
                  padding: 6, cursor: 'pointer', color: 'var(--t3)',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--t1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--t3)'; }}
              >
                <Settings size={22} strokeWidth={1.6} />
              </button>
              {showViewSettings && (
                <>
                  <div onClick={() => setShowViewSettings(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div
                    data-testid="task-view-settings-panel"
                    style={{
                      position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 41,
                      minWidth: 280, padding: 14,
                      background: 'var(--sf)', border: '1.5px solid var(--bd)',
                      borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                    }}
                  >
                    <div className="asa-eyebrow" style={{ marginBottom: 10 }}>Options d&apos;affichage</div>
                    <label style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: 10, borderRadius: 10, cursor: 'pointer',
                      background: showProjects ? 'rgba(91,190,252,0.08)' : 'transparent',
                      border: `1px solid ${showProjects ? '#5BBEFC44' : 'var(--bd)'}`,
                    }}>
                      <input
                        type="checkbox"
                        data-testid="toggle-show-projects"
                        checked={showProjects}
                        onChange={(e) => setShowProjects(e.target.checked)}
                        style={{ marginTop: 2, accentColor: '#5BBEFC' }}
                      />
                      <div>
                        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: 'var(--t1)' }}>
                          Afficher les projets
                        </div>
                        <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 3, lineHeight: 1.4 }}>
                          Voir les cartes projet avec les tâches vidéo groupées. Masqué par défaut.
                        </div>
                      </div>
                    </label>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <p className="asa-subheading" style={{ fontSize: '0.92rem', margin: 0 }}>
          Pilote ton pipeline · suis l&apos;avancée de chaque tâche
        </p>
      </div>

      {/* Iter33 — Custom user picker (founder only) — Iter62ab: hide entirely; the gear is the only entry point */}
      {false && scope === 'custom' && isFounder && (
        <div style={{
          padding: '0 22px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <div className="asa-eyebrow" style={{ color: 'var(--t3)' }}>Voir les tâches de</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {users.filter((u) => u.id !== user.id).map((u) => {
              const active = customUserId === u.id;
              return (
                <button
                  key={u.id}
                  data-testid={`scope-custom-user-${u.id}`}
                  onClick={() => setCustomUserId(active ? '' : u.id)}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    background: active ? 'rgba(91,190,252,0.18)' : 'var(--sf2)',
                    border: active ? '1.5px solid #5BBEFC' : '1.5px solid var(--bd)',
                    color: active ? '#5BBEFC' : 'var(--t2)',
                    fontFamily: 'Sora', fontWeight: 600, fontSize: '0.78rem',
                    cursor: 'pointer',
                  }}
                >
                  {u.name}
                </button>
              );
            })}
          </div>
          {!customUserId && (
            <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)' }}>
              Sélectionne un membre pour voir uniquement ses tâches.
            </div>
          )}
        </div>
      )}

      {/* Iter 62cw — Admin Todo mode : hide the full toolbar (filters/deadline/format/tags/search)
          for a clean Apple-premium look on the founder's personal Todo view. Keep only
          Nouvelle + IA doc + Elevate buttons in a minimal header. */}
      {(() => {
        const isAdminScope = isFounderStrict && layout === 'list' && scope === 'mine';
        if (!isAdminScope) return null;
        const isTodoMode = adminMode === 'todo';
        return (
          <div style={{ padding: '0 22px 8px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Iter 62cx — Switcher "To-Do List" | "Équipe" (admin scope only) */}
            <div
              data-testid="admin-mode-switcher"
              style={{
                display: 'inline-flex', gap: 2, padding: 3, borderRadius: 12,
                background: 'rgba(15,25,50,0.6)',
                border: '1px solid rgba(74,134,232,0.14)',
              }}
            >
              {[
                { id: 'todo', label: 'To-Do List' },
                { id: 'team', label: 'Équipe' },
              ].map((m) => {
                const active = adminMode === m.id;
                return (
                  <button
                    key={m.id}
                    data-testid={`admin-mode-${m.id}`}
                    onClick={() => setAdminMode(m.id)}
                    style={{
                      padding: '8px 18px', borderRadius: 9,
                      background: active ? 'linear-gradient(180deg, #7FADFF 0%, #4A86E8 100%)' : 'transparent',
                      border: 'none',
                      color: active ? '#fff' : 'rgba(200,215,240,0.42)',
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                      cursor: 'pointer',
                      boxShadow: active ? '0 4px 14px -4px rgba(127,173,255,0.55), inset 0 1px 0 rgba(255,255,255,0.24)' : 'none',
                      transition: 'all 0.15s var(--ease-out)',
                    }}
                  >{m.label}</button>
                );
              })}
            </div>
            <div style={{ flex: 1 }} />
            {isTodoMode && (
              <>
                <button
                  data-testid="task-new-btn"
                  onClick={() => setShowAdminCreate(true)}
                  className="asa-btn"
                  title="Nouvelle tâche · To-Do List"
                ><Plus size={16} strokeWidth={2.2} /> Nouvelle tâche · To-Do List</button>
                <button
                  data-testid="tasks-from-doc-btn"
                  onClick={() => setShowFromDoc(true)}
                  className="asa-btn"
                  style={{ background: 'linear-gradient(135deg, #A78BFA, #5BBEFC)', border: 'none' }}
                  title="IA doc · To-Do List"
                ><Sparkles size={14} strokeWidth={2.2} /> IA doc · To-Do List</button>
              </>
            )}
          </div>
        );
      })()}

      {/* Iter47j — Compact toolbar : 4 dropdown buttons on a single line
          [Vue ▾] [Deadline ▾] [Format ▾] [Archives] · Search · Nouvelle · IA */}
      {!(isFounderStrict && layout === 'list' && scope === 'mine' && adminMode === 'todo') && (
      <div style={{ padding: '0 22px 14px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToolbarDropdown
          testId="toolbar-view"
          label="Vue"
          value={
            scope === 'custom' && customUserId
              ? (users.find((u) => u.id === customUserId)?.name?.split(' ')[0] || 'Défaut')
              : (SCOPES.find((s) => s.id === scope)?.label || 'Vue')
          }
          options={SCOPES.filter((sc) => sc.id !== 'custom' || isFounder).map((s) => ({ id: s.id, label: s.label }))}
          onSelect={(v) => { setScope(v); setFilter('all'); }}
          current={scope}
        />
        {/* Iter62aa — Founder gear: set default employee view */}
        {isFounder && (
          <div style={{ position: 'relative' }}>
            <button
              data-testid="tasks-default-scope-gear"
              onClick={() => setShowDefaultScope((v) => !v)}
              title="Choisir l'employé vu par défaut"
              style={{
                width: 34, height: 34, borderRadius: 10,
                background: 'linear-gradient(135deg, rgba(245,166,35,0.14), rgba(245,166,35,0.04))',
                border: '1px solid rgba(245,166,35,0.32)',
                boxShadow: '0 4px 12px -6px rgba(245,166,35,0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
                cursor: 'pointer', display: 'grid', placeItems: 'center',
              }}
            >
              <Settings size={14} color="#F5D08A" strokeWidth={1.9} />
            </button>
            {showDefaultScope && (
              <DefaultScopePopover
                users={users}
                scope={scope}
                userId={customUserId}
                onSave={(newScope, uid) => {
                  if (newScope === 'custom' && uid) {
                    localStorage.setItem('asa_tasks_default_scope', JSON.stringify({ scope: 'custom', userId: uid }));
                    setScope('custom');
                    setCustomUserId(uid);
                    toast.success(`Vue par défaut : ${users.find((u) => u.id === uid)?.name || uid}`);
                  } else {
                    localStorage.removeItem('asa_tasks_default_scope');
                    setScope('mine');
                    setCustomUserId('');
                    toast.success('Vue par défaut : tes propres tâches');
                  }
                  setShowDefaultScope(false);
                }}
                onClose={() => setShowDefaultScope(false)}
              />
            )}
          </div>
        )}
        <ToolbarDropdown
          testId="toolbar-deadline"
          label="Deadline"
          value={
            timeView === 'day' ? "Aujourd'hui"
            : timeView === 'week' ? 'Cette semaine'
            : timeView === 'month' ? 'Ce mois'
            : 'Tout'
          }
          options={[
            { id: 'day',   label: "Aujourd'hui" },
            { id: 'week',  label: 'Cette semaine' },
            { id: 'month', label: 'Ce mois' },
            { id: 'all',   label: 'Tout' },
          ]}
          onSelect={setTimeView}
          current={timeView}
        />
        <ToolbarDropdown
          testId="toolbar-format"
          label="Format"
          value={layout === 'list' ? 'Liste' : layout === 'calendar' ? 'Calendrier' : 'Bilan'}
          options={[
            { id: 'list',     label: 'Liste' },
            { id: 'calendar', label: 'Calendrier' },
            { id: 'bilan',    label: 'Bilan' },
          ]}
          onSelect={setLayout}
          current={layout}
        />
        {/* Iter47x — Category tag filter */}
        <ToolbarDropdown
          testId="toolbar-tag"
          label="Tag"
          value={categoryFilter ? CATEGORY_TAGS[categoryFilter].label : 'Toutes catégories'}
          options={[
            { id: '__all', label: 'Toutes catégories' },
            ...CATEGORY_TAG_ORDER
              .filter((k) => user?.id !== 'axel' || !AXEL_HIDDEN_TAGS.includes(k))
              .map((k) => ({ id: k, label: CATEGORY_TAGS[k].label })),
          ]}
          onSelect={(v) => setCategoryFilter(v === '__all' ? null : v)}
          current={categoryFilter || '__all'}
        />

        <div style={{ flex: 1, minWidth: 180 }}>
          <div className="asa-field">
            <Search size={15} color="var(--t3)" strokeWidth={1.6} />
            <input
              data-testid="task-search"
              placeholder="Rechercher une tâche…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
            />
            {search && (
              <button onClick={() => setSearch('')}>
                <X size={14} color="var(--t3)" />
              </button>
            )}
          </div>
        </div>

        <button
          data-testid="task-new-btn"
          onClick={() => setShowCreate(true)}
          className="asa-btn"
        >
          <Plus size={16} strokeWidth={2.2} /> Nouvelle
        </button>
        {isFounder && (
          <button
            data-testid="tasks-from-doc-btn"
            onClick={() => setShowFromDoc(true)}
            className="asa-btn"
            style={{
              background: 'linear-gradient(135deg, #A78BFA, #5BBEFC)',
              border: 'none',
            }}
            title="Génère des tâches depuis un document via IA"
          >
            <Sparkles size={14} strokeWidth={2.2} /> IA doc
          </button>
        )}
        {isFounder && (
          <button
            data-testid="task-new-elevate-btn"
            onClick={() => setShowCreateElevate(true)}
            className="asa-btn"
            style={{
              background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
              border: 'none',
              color: '#0B1330',
            }}
            title="Créer une tâche dans Elevate Influence School"
          >
            <Zap size={14} strokeWidth={2.4} /> Elevate
          </button>
        )}
      </div>
      )}

      {/* Status filter chips */}
      {!(isFounderStrict && layout === 'list' && scope === 'mine' && adminMode === 'todo') && (
      <div
        className="no-scrollbar"
        style={{
          display: 'flex', gap: 8, padding: '0 22px 16px',
          overflowX: 'auto',
        }}
      >
        {FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '7px 14px', borderRadius: 999,
                background: active ? `${f.color}1F` : 'var(--sf)',
                border: `1.5px solid ${active ? `${f.color}55` : 'var(--bd)'}`,
                color: active ? 'var(--t1)' : 'var(--t2)',
                fontFamily: 'Sora', fontWeight: 600, fontSize: '0.78rem',
                whiteSpace: 'nowrap', transition: 'all 0.2s var(--ease-out)',
              }}
            >
              <span style={{
                width: 6, height: 6, borderRadius: '50%', background: f.color,
              }} />
              {f.label}
              {/* Iter43 — count badge retiré (anti-anxiété : user's request) */}
            </button>
          );
        })}
      </div>
      )}

      {/* List or Calendar view */}
      <div style={{ padding: '0 22px 120px' }}>
        {/* Iter 62cw — Team switcher (founder strict only) : click sur un membre pour voir SA vue (ancien design). Click sur soi ou "Moi" → nouvelle Admin Todo view. */}
        {isFounderStrict && layout === 'list' && (
          <TeamSwitcher
            users={users}
            currentScope={scope}
            currentCustomUserId={customUserId}
            currentUserId={user?.id}
            onSelectMine={() => { setScope('mine'); setCustomUserId(''); }}
            onSelectUser={(uid) => { setScope('custom'); setCustomUserId(uid); }}
          />
        )}
        {/* Iter 62cy — Elevate card retirée de tout l'onglet Tâches (trop d'infos). */}
        {false && isFounderStrict && showElevateInTasks && !(scope === 'mine' && layout === 'list') && (
          <div style={{ marginBottom: 14 }}>
            <ElevatePendingBanner />
            <ElevateInfluenceCard />
          </div>
        )}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="shimmer" style={{ height: 70, borderRadius: 14 }} />
            ))}
          </div>
        ) : layout === 'calendar' ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <button
                data-testid="calendar-sync-now"
                onClick={reload}
                disabled={loading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 8,
                  background: 'rgba(52,211,153,0.12)',
                  border: '1px solid rgba(52,211,153,0.35)',
                  color: '#34D399',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                  cursor: loading ? 'wait' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
                title="Recharger les tâches"
              >
                {loading ? <Loader2 size={12} className="spin" /> : <CalendarIcon size={12} />}
                Synchroniser maintenant
              </button>
            </div>
            <MonthCalendar
              tasks={filtered}
              projects={projects}
              cursor={calCursor}
              onPrev={() => setCalCursor(new Date(calCursor.getFullYear(), calCursor.getMonth() - 1, 1))}
              onNext={() => setCalCursor(new Date(calCursor.getFullYear(), calCursor.getMonth() + 1, 1))}
              onSelect={setSelected}
            />
          </>
        ) : layout === 'bilan' ? (
          <TasksBilanView />
        ) : (isFounderStrict && scope === 'mine' && layout === 'list' && adminMode === 'todo') ? (
          /* Iter 62cw — Admin Todo View : nouvelle liste dédiée Rayan & Hugo */
          <AdminTodoView
            tasks={filtered}
            users={users}
            currentUser={user}
            onOpenTask={(t) => setAdminEditTask(t)}
            onCreateTask={(defaults) => {
              // Iter 62cx — En mode To-Do List, ouvrir le nouveau modal premium
              if (defaults?.assigned_to) {
                try { localStorage.setItem('asa_new_task_assigned', defaults.assigned_to); } catch { /* noop */ }
              }
              setShowAdminCreate(true);
            }}
            onDeleteTask={async (t) => {
              if (!window.confirm(`Supprimer « ${t.title} » ?`)) return;
              try {
                await api.delete(`/tasks/${t.id}`);
                toast.success('Tâche supprimée');
                reload();
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Erreur');
              }
            }}
            reload={reload}
          />
        ) : filtered.length === 0 ? (
          <EmptyState search={search} filter={filter} onCreate={() => setShowCreate(true)} />
        ) : scope === 'client' ? (
          <div className="stagger" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            {byClient.map((cl) => (
              <div key={cl.name}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '6px 4px 10px', marginBottom: 6,
                  borderBottom: `1.5px solid ${cl.color}33`,
                }}>
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%', background: cl.color,
                    boxShadow: `0 0 0 3px ${cl.color}22`,
                  }} />
                  <span style={{
                    fontFamily: 'Sora', fontWeight: 800, fontSize: '0.95rem',
                    color: 'var(--t1)', letterSpacing: '-0.01em',
                  }}>{cl.name}</span>
                  <span style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem',
                    padding: '2px 8px', borderRadius: 999,
                    background: `${cl.color}1F`, color: cl.color,
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>
                    {cl.projects.length} projet{cl.projects.length > 1 ? 's' : ''}
                    {cl.tasks.length ? ` · ${cl.tasks.length} tâche${cl.tasks.length > 1 ? 's' : ''}` : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {cl.projects.map(({ project, tasks: ptasks }) => (
                    // Iter43h — Always use MonteurProjectCard so founders also get pencils on video tasks
                    (
                      <MonteurProjectCard
                        key={project.id}
                        project={project}
                        tasks={ptasks}
                        users={users}
                        onClick={() => openProject(project.id)}
                        onTaskClick={(t) => setSelected(t)}
                        onEdit={setEditingTask}
                      />
                    )
                  ))}
                  {(cl.groups || []).map((g) => (
                    <GroupCard
                      key={g.id}
                      group={g}
                      steps={grouped.stepsByGroup.get(g.id) || []}
                      users={users}
                      canManage={isFounder || g.created_by === user.id || g.assigned_to === user.id}
                      onStepClick={(step) => setSelected(step)}
                      onEditStep={(step) => setEditingTask(step)}
                      onRenameGroup={async (gid, newName) => {
                        try {
                          await api.patch(`/tasks/${gid}`, { title: newName });
                          toast.success('Groupe renommé');
                          reload();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Erreur');
                        }
                      }}
                      onAddStep={async (gid, title) => {
                        try {
                          await api.post(`/tasks/groups/${gid}/steps`, { title });
                          toast.success('Étape ajoutée');
                          reload();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Erreur');
                        }
                      }}
                      onDeleteGroup={async (gid) => {
                        try {
                          await api.delete(`/tasks/groups/${gid}`);
                          toast.success('Groupe supprimé');
                          reload();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Erreur');
                        }
                      }}
                    />
                  ))}
                  {cl.tasks.map((t, idx) => (
                    <TaskRow key={t.id} idx={idx + 1} task={t} users={users} onClick={() => setSelected(t)} onEdit={setEditingTask} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="stagger" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            {/* ═══════════════════════════════════════════════════════════════
                SECTION 1 — AUJOURD'HUI (ou horizon sélectionné)
                Vidéos du jour (rouges, #1, #2, …) puis tâches classiques
                dans une seule numérotation continue.
                ═══════════════════════════════════════════════════════════════ */}
            {(todayVideos.length + grouped.orphans.length) > 0 && (
              <section>
                <SectionHeader
                  label={
                    timeView === 'day' ? "Aujourd'hui"
                    : timeView === 'week' ? 'Cette semaine'
                    : timeView === 'month' ? 'Ce mois'
                    : 'Toutes les tâches'
                  }
                  count={todayVideos.length + grouped.orphans.length}
                  accent={todayVideos.length > 0 ? '#EF4444' : '#5BBEFC'}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {todayVideos.map((t, idx) => (
                    <TaskRow
                      key={`priority-${t.id}`}
                      idx={idx + 1}
                      task={t}
                      users={users}
                      onClick={() => setSelected(t)}
                      onEdit={setEditingTask}
                    />
                  ))}
                  {grouped.orphans.length > 0 && (
                    <TaskListDnd
                      tasks={grouped.orphans}
                      enabled={isFounder}
                      users={users}
                      startIdx={todayVideos.length}
                      onClick={setSelected}
                      onEdit={setEditingTask}
                      onReordered={(newIds) => {
                        api.post('/tasks/reorder', { order: newIds })
                          .then(() => reload())
                          .catch(() => toast.error('Erreur de réordonnancement'));
                      }}
                    />
                  )}
                </div>
              </section>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                SECTION 2 — TÂCHES GROUPÉES (poupée russe)
                ═══════════════════════════════════════════════════════════════ */}
            {grouped.groups.length > 0 && (() => {
              // Iter47l — Groups grouped BY CLIENT (Nash, Hakim, Benjamin, Raphaël, …)
              const byClientMap = new Map();
              grouped.groups.forEach((g) => {
                const clientKey = (g.client_name || 'Sans client').trim();
                if (!byClientMap.has(clientKey)) byClientMap.set(clientKey, { name: clientKey, color: g.group_color || '#A78BFA', groups: [] });
                byClientMap.get(clientKey).groups.push(g);
              });
              const clientBuckets = Array.from(byClientMap.values()).sort((a, b) => {
                if (a.name === 'Sans client') return 1;
                if (b.name === 'Sans client') return -1;
                return a.name.localeCompare(b.name);
              });
              return (
                <section>
                  <SectionHeader
                    label="Tâches groupées"
                    count={grouped.groups.length}
                    accent="#A78BFA"
                    icon={LayoutGrid}
                  />
                  {clientBuckets.map((bucket) => (
                    <div key={bucket.name} data-testid={`groups-client-${bucket.name}`} style={{ marginBottom: 18 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '4px 4px 10px', marginBottom: 4,
                      }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: bucket.color }} />
                        <span style={{
                          fontFamily: 'Sora', fontWeight: 700, fontSize: '0.72rem',
                          color: 'var(--t2)', letterSpacing: '0.05em', textTransform: 'uppercase',
                        }}>{bucket.name}</span>
                        <span style={{
                          fontFamily: 'Sora', fontWeight: 700, fontSize: '0.6rem',
                          padding: '1px 7px', borderRadius: 999,
                          background: `${bucket.color}1F`, color: bucket.color,
                        }}>{bucket.groups.length}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {bucket.groups.map((g) => (
                          <GroupCard
                            key={g.id}
                            group={g}
                            steps={grouped.stepsByGroup.get(g.id) || []}
                            users={users}
                            canManage={isFounder || g.created_by === user.id || g.assigned_to === user.id}
                            onStepClick={(step) => setSelected(step)}
                            onEditStep={(step) => setEditingTask(step)}
                            onRenameGroup={async (gid, newName) => {
                              try {
                                await api.patch(`/tasks/${gid}`, { title: newName });
                                toast.success('Groupe renommé');
                                reload();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || 'Erreur');
                              }
                            }}
                            onAddStep={async (gid, title) => {
                              try {
                                await api.post(`/tasks/groups/${gid}/steps`, { title });
                                toast.success('Étape ajoutée');
                                reload();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || 'Erreur');
                              }
                            }}
                            onDeleteGroup={async (gid) => {
                              try {
                                await api.delete(`/tasks/groups/${gid}`);
                                toast.success('Groupe supprimé');
                                reload();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || 'Erreur');
                              }
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </section>
              );
            })()}

            {/* ═══════════════════════════════════════════════════════════════
                SECTION 3 — PROJETS (Iter47i)
                Masquée par défaut. Founders uniquement, via ⚙ Options.
                Les monteurs ne voient jamais les projets ici : ils travaillent
                à la tâche, pas au projet. C'est la vision Rayan.
                ═══════════════════════════════════════════════════════════════ */}
            {isFounder && showProjects && grouped.projects.length > 0 && (
              <section>
                <SectionHeader
                  label="Projets · tâches par vidéo"
                  count={grouped.projects.length}
                  accent="#5BBEFC"
                  icon={Folder}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {grouped.projects.map(({ project, tasks: ptasks }) => (
                    <MonteurProjectCard
                      key={project.id}
                      project={project}
                      tasks={ptasks}
                      users={users}
                      onClick={() => openProject(project.id)}
                      onTaskClick={(t) => setSelected(t)}
                      onEdit={setEditingTask}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateTaskFlow
          users={users}
          defaultAssignee={scope === 'custom' && customUserId ? customUserId : null}
          onClose={() => setShowCreate(false)}
          onSaved={() => { reload(); setShowCreate(false); }}
        />
      )}

      {/* Iter 62cx — Nouveau modal premium "To-Do List" (Rayan/Hugo) */}
      {showAdminCreate && (
        <AdminTodoTaskDialog
          onClose={() => setShowAdminCreate(false)}
          onSaved={() => { reload(); setShowAdminCreate(false); }}
        />
      )}

      {/* Iter 62cz — Modal d'édition d'une tâche admin_todo */}
      {adminEditTask && (
        <AdminTodoTaskDialog
          task={adminEditTask}
          onClose={() => setAdminEditTask(null)}
          onSaved={() => { reload(); setAdminEditTask(null); }}
          onDeleted={() => { reload(); setAdminEditTask(null); }}
        />
      )}

      {showFromDoc && (
        <TasksFromDocDialog
          users={users}
          onClose={() => setShowFromDoc(false)}
          onSaved={() => { reload(); setShowFromDoc(false); }}
        />
      )}

      {showCreateElevate && (
        <CreateElevateTaskModal
          onClose={() => setShowCreateElevate(false)}
          onCreated={() => {
            // Iter 62ba — force refresh of ElevateInfluenceCard + PlanningPage
            window.dispatchEvent(new CustomEvent('asa-elevate-refresh'));
          }}
        />
      )}

      {selected && (
        <DetailSidebar
          task={selected}
          users={users}
          isFounder={isFounder}
          onClose={() => setSelected(null)}
          onSaved={(updated) => {
            // Iter41 (post-deploy) — optimistic update : if the caller provided
            // the fresh task (e.g. phase-step / PATCH), patch local state immediately
            // so the UI shows the next step without waiting for the reload.
            if (updated && updated.id) {
              setSelected((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
              setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
            } else {
              reload();
            }
          }}
          onDelete={() => { reload(); setSelected(null); }}
        />
      )}

      {/* Iter43h — Quick edit modal for ANY task (video or normal, any stage) */}
      {editingTask && (
        <QuickTaskEditModal
          task={editingTask}
          users={users}
          onClose={() => setEditingTask(null)}
          onSaved={(updated) => {
            if (updated && updated.id) {
              setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
            } else {
              reload();
            }
            setEditingTask(null);
          }}
          onSilentUpdate={(updated) => {
            // Iter61r — Frame.io inline updates: refresh list WITHOUT closing the modal
            if (updated && updated.id) {
              setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
            }
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project group card — one card per project (clicks → /projects)
// ---------------------------------------------------------------------------
function ProjectGroupCard({ project, tasks, onClick }) {
  const done = tasks.filter((t) => t.stage === 'done').length;
  const total = tasks.length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <button
      data-testid={`project-group-${project.id}`}
      onClick={onClick}
      className="asa-card row-card"
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px', textAlign: 'left', width: '100%',
        cursor: 'pointer',
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: `${project.color}1A`, border: `1.5px solid ${project.color}55`,
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <ProjectIcon name={project.icon} size={20} color={project.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '1rem', color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {project.name}
          </span>
          <span style={{
            fontFamily: 'Sora', fontWeight: 600, fontSize: '0.58rem',
            padding: '2px 8px', borderRadius: 999,
            background: `${project.color}1F`, color: project.color,
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>Projet</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, maxWidth: 200, height: 5, borderRadius: 3, background: 'var(--sf2)', overflow: 'hidden' }}>
            <div style={{
              width: `${pct}%`, height: '100%',
              background: `linear-gradient(90deg, ${project.color}, ${project.color}AA)`,
              transition: 'width 0.4s var(--ease-out)',
            }} />
          </div>
          <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.74rem', color: project.color }}>
            {pct}%
          </span>
          <span style={{ fontFamily: 'Lexend', fontSize: '0.7rem', color: 'var(--t3)' }}>
            {done}/{total} étape{total > 1 ? 's' : ''}
          </span>
          {project.client && (
            <>
              <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--t3)' }} />
              <span style={{ fontFamily: 'Lexend', fontSize: '0.7rem', color: 'var(--t3)' }}>
                {project.client}
              </span>
            </>
          )}
        </div>
      </div>
      <ChevronRight size={16} color="var(--t3)" style={{ flexShrink: 0 }} />
    </button>
  );
}

// ---------------------------------------------------------------------------
// MonteurProjectCard — simplified view for contributors (N-1) on Montage projects
// Shows: "X vidéos à monter — 1/jour · rendu 17h · vérif 17h-19h"
// ---------------------------------------------------------------------------
function MonteurProjectCard({ project, tasks, onClick, onTaskClick, onEdit, users }) {
  const [expanded, setExpanded] = useState(false);
  const isMontage = (project.tags || []).includes('framework:montage') || project.framework === 'montage';
  const videoCount = isMontage
    ? (project.video_count || (project.fiches?.length || 0) || tasks.length)
    : tasks.length;
  const macrosDone = tasks.filter((t) => t.stage === 'done').length;
  const macrosTotal = tasks.length;
  const pct = macrosTotal ? Math.round((macrosDone / macrosTotal) * 100) : 0;

  return (
    <div data-testid={`monteur-card-${project.id}`} className="asa-card" style={{ overflow: 'hidden', padding: 0 }}>
      <button
        onClick={() => setExpanded((e) => !e)}
        data-testid={`monteur-expand-${project.id}`}
        className="row-card"
        style={{
          display: 'flex', alignItems: 'stretch', gap: 0,
          padding: 0, textAlign: 'left', width: '100%',
          cursor: 'pointer', background: 'transparent', border: 'none',
        }}
      >
        <div style={{
          width: 5, flexShrink: 0,
          background: `linear-gradient(180deg, ${project.color}, ${project.color}66)`,
        }} />
        <div style={{ flex: 1, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: `${project.color}1A`, border: `1.5px solid ${project.color}55`,
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <ProjectIcon name={project.icon} size={20} color={project.color} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{
                fontFamily: 'Sora', fontWeight: 700, fontSize: '1rem', color: 'var(--t1)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{project.name}</span>
              {project.client && (
                <span style={{
                  fontFamily: 'Sora', fontWeight: 600, fontSize: '0.58rem',
                  padding: '2px 8px', borderRadius: 999,
                  background: 'var(--sf2)', color: 'var(--t3)',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>{project.client}</span>
              )}
            </div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: '1.2rem',
              color: project.color, letterSpacing: '-0.02em', lineHeight: 1.1,
              marginBottom: 4,
            }}>
              {videoCount} {isMontage ? 'vidéo' : 'tâche'}{videoCount > 1 ? 's' : ''} à monter
            </div>
            {isMontage && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
                fontFamily: 'Lexend', fontSize: '0.78rem', color: 'var(--t2)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Clock size={12} color="var(--t3)" /> 1 vidéo / jour
                </span>
                <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--t3)' }} />
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Check size={12} color="#34D399" /> rendu <strong style={{ color: '#34D399' }}>17h</strong>
                </span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <div style={{ flex: 1, maxWidth: 260, height: 6, borderRadius: 3, background: 'var(--sf2)', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%',
                  background: `linear-gradient(90deg, ${project.color}, ${project.color}AA)`,
                  transition: 'width 0.4s var(--ease-out)' }} />
              </div>
              <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.74rem', color: project.color }}>
                {macrosDone}/{macrosTotal} étapes
              </span>
            </div>
          </div>
          <ChevronDown
            size={16}
            color="var(--t3)"
            style={{
              flexShrink: 0,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s var(--ease-out)',
            }}
          />
        </div>
      </button>

      {expanded && (
        <div
          data-testid={`monteur-tasks-${project.id}`}
          style={{
            padding: '0 18px 14px 28px',
            display: 'flex', flexDirection: 'column', gap: 6,
            background: 'rgba(46,163,242,0.03)',
          }}
        >
          {tasks.length === 0 ? (
            <div style={{ padding: '12px 0', fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', fontStyle: 'italic' }}>
              Aucune tâche à monter pour le moment.
            </div>
          ) : (
            tasks.map((t, idx) => (
              <TaskRow key={t.id} idx={idx + 1} task={t} users={users} onClick={() => onTaskClick(t)} onEdit={onEdit} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task list row (numbered, with icon and inline status)
// ---------------------------------------------------------------------------
// Iter61w — Task chrono helpers (start button + running timer + final duration)
export function formatDuration(seconds) {
  if (seconds == null || seconds < 0) return '';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}

function useNowSecond(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [active]);
  return now;
}

// Compact status pill shown on TaskRow.
// Iter61w — visible for EVERYONE (founders + contributors) so anyone can see at a glance
// if the task is "Pas commencé", running (with live chrono for founders / "Commencé" for others),
// or "Terminée" via the existing stage pill.
// Iter61y — chrono now cumulative: duration_seconds is the accumulated total across sessions;
// when running (started_at set) we add the current session on top.
// Renders its own leading divider so the parent doesn't have to gate it.
export function TaskChronoBadge({ task }) {
  const { isFounder } = useAuth();
  const running = !!task.started_at && task.stage !== 'done';
  // "Pas commencé" = never started (no first_started_at, no started_at, no accumulated time)
  const notStarted = !task.started_at && !task.first_started_at && !task.duration_seconds && task.stage !== 'done';
  // "En attente de validation" (paused chrono) = has history but no active session, not done
  const paused = !running && !notStarted && task.stage !== 'done' && (task.duration_seconds || task.first_started_at);
  const finished = task.stage === 'done' && !!task.duration_seconds;
  const now = useNowSecond(running && isFounder);

  // Nothing to show if done without a chrono trail
  if (!running && !notStarted && !finished && !paused) return null;

  let content = null;
  if (notStarted) {
    content = (
      <span
        data-testid={`task-status-notstarted-${task.id}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '2px 8px', borderRadius: 999,
          background: 'rgba(180,196,220,0.10)', border: '1px solid rgba(180,196,220,0.28)',
          color: 'var(--t3)',
          fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem',
          letterSpacing: '0.02em', textTransform: 'uppercase',
        }}
      >
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--t4)' }} />
        Pas commencé
      </span>
    );
  } else if (running) {
    // Founder sees the actual live chrono (accumulated + current session); contributor sees a "Commencé" pill
    if (isFounder) {
      const sessionSec = Math.max(0, Math.floor((now - new Date(task.started_at).getTime()) / 1000));
      const totalSec = (task.duration_seconds || 0) + sessionSec;
      content = (
        <span
          data-testid={`task-chrono-${task.id}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 999,
            background: 'rgba(245,166,35,0.16)', border: '1px solid rgba(245,166,35,0.42)',
            color: '#F5A623',
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem',
            letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums',
          }}
          title={`Démarrée à ${new Date(task.started_at).toLocaleTimeString('fr-FR')}`}
        >
          ⏱ {formatDuration(totalSec)}
        </span>
      );
    } else {
      content = (
        <span
          data-testid={`task-status-started-${task.id}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '2px 8px', borderRadius: 999,
            background: 'rgba(52,211,153,0.16)', border: '1px solid rgba(52,211,153,0.42)',
            color: '#34D399',
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem',
            letterSpacing: '0.02em', textTransform: 'uppercase',
          }}
        >
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#34D399' }} />
          Commencé
        </span>
      );
    }
  } else if (paused && isFounder) {
    // Iter61y — Founder sees the accumulated time in "paused" state (waiting for review or rejected)
    content = (
      <span
        data-testid={`task-chrono-paused-${task.id}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 8px', borderRadius: 999,
          background: 'rgba(180,196,220,0.10)', border: '1px solid rgba(180,196,220,0.35)',
          color: 'var(--t2)',
          fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem',
          letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums',
        }}
        title="Chrono en pause — en attente de validation"
      >
        ⏸ {formatDuration(task.duration_seconds || 0)}
      </span>
    );
  } else if (finished && isFounder) {
    // Only founders see the final duration inline on the list
    content = (
      <span
        data-testid={`task-chrono-${task.id}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 8px', borderRadius: 999,
          background: 'rgba(91,190,252,0.14)', border: '1px solid rgba(91,190,252,0.30)',
          color: '#5BBEFC',
          fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem',
          letterSpacing: '0.02em',
        }}
        title="Durée totale"
      >
        ✓ {formatDuration(task.duration_seconds)}
      </span>
    );
  }

  if (!content) return null;

  return (
    <>
      <span style={{ width: 1, height: 9, background: 'var(--bd)' }} />
      {content}
    </>
  );
}

// Sticky bottom bar shown inside DetailSidebar.
// Axel (contributor / assignee) → just buttons ("Commencer" / "J'ai terminé"), NEVER sees the chrono.
// Founders → passive hint if not started, running chrono if in progress, final duration if done.
// Iter61y — Chrono is now cumulative across sessions (survives rejects). Contributor "J'ai terminé"
// pauses the chrono so they can start another task while awaiting founder validation.
export function TaskChronoBar({ task, currentUserId, isFounder, onChanged }) {
  const assignees = new Set(task.assignees || []);
  for (const k of ['assigned_to', 'assigned_to_derush', 'assigned_to_montage']) {
    if (task[k]) assignees.add(task[k]);
  }
  const isAssignee = currentUserId && assignees.has(currentUserId);
  const isDone = task.stage === 'done';
  const isRunning = !!task.started_at && !isDone;
  // Was the task ever started ? (accumulated duration OR first_started_at OR current session)
  const hasChronoHistory = !!task.first_started_at || !!task.duration_seconds || !!task.started_at;
  const isCompleted = isDone && !!task.duration_seconds;
  const isPausedForReview = task.stage === 'review' && !isRunning && hasChronoHistory;
  // Only founders see the live tick — contributors never see the chrono
  const now = useNowSecond(isRunning && isFounder);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${task.id}/start`);
      toast.success('Tâche démarrée');
      onChanged?.(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && typeof detail === 'object' && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erreur');
      }
    } finally {
      setBusy(false);
    }
  };

  const markDone = async () => {
    if (busy) return;
    // Iter61ad — For Frame.io tasks, force the monteur to submit the link first.
    if (task.frameio_enabled && !task.frameio_link) {
      toast.error('Ajoute d\'abord le lien Frame.io avant de valider la tâche');
      return;
    }
    setBusy(true);
    try {
      // Iter61z — If task doesn't require founder validation, contributor closes it directly.
      // If it does, we move to stage=review and wait for the founder decision.
      const nextStage = task.requires_validation ? 'review' : 'done';
      const r = await api.patch(`/tasks/${task.id}`, { stage: nextStage });
      toast.success(
        nextStage === 'done'
          ? 'Tâche terminée ✅'
          : 'Envoyée en validation',
      );
      onChanged?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  // ===== CONTRIBUTOR (assignee) view =====
  if (isAssignee && !isFounder) {
    if (isDone) return null;
    // Case A: not currently running (never started OR paused after rejection) → "Commencer"
    if (!isRunning && task.stage !== 'review') {
      return (
        <button
          data-testid="task-chrono-start-btn"
          onClick={start}
          disabled={busy}
          style={{
            position: 'sticky', bottom: 0, left: 0, right: 0, marginTop: 20,
            width: '100%', padding: '14px 16px', borderRadius: 14,
            background: busy ? 'var(--sf2)' : 'linear-gradient(90deg, #2EA3F2 0%, #5BBEFC 100%)',
            border: 'none', color: '#fff',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 14,
            cursor: busy ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 10,
            boxShadow: '0 10px 30px -8px rgba(46,163,242,0.55)',
          }}
        >
          <span style={{
            width: 30, height: 30, borderRadius: '50%',
            background: 'rgba(255,255,255,0.20)',
            display: 'grid', placeItems: 'center', fontSize: 15,
          }}>▶</span>
          <span style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ fontSize: 13.5 }}>{hasChronoHistory ? 'Reprendre la tâche' : 'Commencer la tâche'}</div>
            <div style={{ fontSize: 11, opacity: 0.80, fontWeight: 600 }}>Toucher quand tu {hasChronoHistory ? 'reprends' : 'commences'}</div>
          </span>
        </button>
      );
    }
    // Case B: currently running → "J'ai terminé"
    if (isRunning) {
      return (
        <button
          data-testid="task-chrono-finish-btn"
          onClick={markDone}
          disabled={busy}
          style={{
            position: 'sticky', bottom: 0, left: 0, right: 0, marginTop: 20,
            width: '100%', padding: '14px 16px', borderRadius: 14,
            background: busy ? 'var(--sf2)' : 'linear-gradient(90deg, #22c55e 0%, #34D399 100%)',
            border: 'none', color: '#fff',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 14,
            cursor: busy ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 10,
            boxShadow: '0 10px 30px -8px rgba(34,197,94,0.55)',
          }}
        >
          <span style={{
            width: 30, height: 30, borderRadius: '50%',
            background: 'rgba(255,255,255,0.20)',
            display: 'grid', placeItems: 'center', fontSize: 15,
          }}>✓</span>
          <span style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ fontSize: 13.5 }}>J&apos;ai terminé la tâche</div>
            <div style={{ fontSize: 11, opacity: 0.80, fontWeight: 600 }}>
              {task.requires_validation
                ? 'Envoyer en validation'
                : 'Clôturer la tâche maintenant'}
            </div>
          </span>
        </button>
      );
    }
    // Case C: stage=review, awaiting founder validation
    return (
      <div
        data-testid="task-chrono-bar-waiting"
        style={{
          position: 'sticky', bottom: 0, left: 0, right: 0, marginTop: 20,
          padding: '12px 16px', borderRadius: 14,
          background: 'rgba(245,166,35,0.10)', border: '1.5px solid rgba(245,166,35,0.30)',
          fontFamily: 'Lexend', fontSize: 12.5, color: '#F5A623',
          textAlign: 'center', fontWeight: 600,
        }}
      >
        En attente de validation
      </div>
    );
  }

  // ===== FOUNDER view (or any non-assignee viewing) =====
  // Iter61y — Chrono is cumulative: (previous accumulated) + (current session if running)
  let seconds = 0;
  if (isRunning) {
    const sessionSec = Math.max(0, Math.floor((now - new Date(task.started_at).getTime()) / 1000));
    seconds = (task.duration_seconds || 0) + sessionSec;
  } else if (isCompleted) {
    seconds = task.duration_seconds || 0;
  } else if (isPausedForReview) {
    seconds = task.duration_seconds || 0;
  }

  // Non-founder + non-assignee → hide entirely
  if (!isFounder && !isAssignee) return null;

  // Final duration (task done with a chrono)
  if (isCompleted) {
    return (
      <div
        data-testid="task-chrono-bar-completed"
        style={{
          position: 'sticky', bottom: 0, left: 0, right: 0, marginTop: 20,
          padding: '14px 16px', borderRadius: 14,
          background: 'rgba(52,211,153,0.10)', border: '1.5px solid rgba(52,211,153,0.35)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'rgba(52,211,153,0.20)',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}><Check size={18} color="#34D399" /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5, color: '#34D399' }}>
            Tâche terminée
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t2)' }}>
            Durée totale : <strong>{formatDuration(seconds)}</strong>
          </div>
        </div>
      </div>
    );
  }

  // Running chrono (founder-only visibility)
  if (isRunning) {
    return (
      <div
        data-testid="task-chrono-bar-running"
        style={{
          position: 'sticky', bottom: 0, left: 0, right: 0, marginTop: 20,
          padding: '14px 16px', borderRadius: 14,
          background: 'rgba(245,166,35,0.10)', border: '1.5px solid rgba(245,166,35,0.35)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'rgba(245,166,35,0.20)',
          display: 'grid', placeItems: 'center', flexShrink: 0, fontSize: 18,
        }}>⏱</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5, color: '#F5A623' }}>
            En cours
          </div>
          <div style={{ fontFamily: 'Lexend', fontVariantNumeric: 'tabular-nums', fontSize: 15, color: 'var(--t1)', fontWeight: 700 }}>
            {formatDuration(seconds)}
          </div>
        </div>
      </div>
    );
  }

  // Iter61y — Paused (contributor sent to review, awaiting founder decision) → founder sees accumulated time
  if (isPausedForReview && isFounder) {
    return (
      <div
        data-testid="task-chrono-bar-paused"
        style={{
          position: 'sticky', bottom: 0, left: 0, right: 0, marginTop: 20,
          padding: '14px 16px', borderRadius: 14,
          background: 'rgba(180,196,220,0.08)', border: '1.5px solid rgba(180,196,220,0.35)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'rgba(180,196,220,0.20)',
          display: 'grid', placeItems: 'center', flexShrink: 0, fontSize: 18,
        }}>⏸</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5, color: 'var(--t1)' }}>
            En attente de validation
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t2)' }}>
            Temps cumulé : <strong>{formatDuration(seconds)}</strong>
          </div>
        </div>
      </div>
    );
  }

  // Founder + not started → passive hint
  if (isFounder && !isDone) {
    return (
      <div
        data-testid="task-chrono-bar-idle"
        style={{
          position: 'sticky', bottom: 0, left: 0, right: 0, marginTop: 20,
          padding: '12px 16px', borderRadius: 14,
          background: 'var(--sf2)', border: '1.5px dashed var(--bd)',
          fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)',
          textAlign: 'center',
        }}
      >
        En attente de démarrage par le monteur
      </div>
    );
  }

  return null;
}


function TaskRow({ idx, task, users, onClick, onEdit, dragHandleProps, isDragging }) {
  const s = STAGES[task.stage] || STAGES.todo;
  const p = PRIORITIES[task.priority] || PRIORITIES.medium;
  const assignee = users.find((u) => u.id === task.assigned_to);
  const due = task.due_date ? parseLocalDate(task.due_date) : null;
  const overdue = due && due < new Date() && task.stage !== 'done';
  const isDone = task.stage === 'done';
  const isVideo = task.kind === 'video';
  const cardRef = useRef(null);
  const doneMinutesLeft = (() => {
    if (!isDone || !task.done_at) return null;
    const elapsedMs = Date.now() - new Date(task.done_at).getTime();
    const remaining = 5 * 60 * 1000 - elapsedMs;
    return remaining > 0 ? Math.max(1, Math.ceil(remaining / 60000)) : 0;
  })();

  // Iter62m — visionOS-style 3D tilt on hover (mouse-tracking)
  const handleMouseMove = (e) => {
    const el = cardRef.current; if (!el || isDragging) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    // Task rows are wide+short — smaller Y tilt, gentle X tilt
    el.style.transform = `perspective(1200px) rotateX(${(-y * 2.5).toFixed(2)}deg) rotateY(${(x * 3.5).toFixed(2)}deg) translateZ(0)`;
    // Shine follows cursor
    const px = ((e.clientX - r.left) / r.width) * 100;
    const py = ((e.clientY - r.top) / r.height) * 100;
    el.style.setProperty('--shine-x', `${px}%`);
    el.style.setProperty('--shine-y', `${py}%`);
  };
  const handleMouseLeave = () => {
    const el = cardRef.current; if (!el) return;
    el.style.transform = 'perspective(1200px) rotateX(0) rotateY(0) translateZ(0)';
    el.style.setProperty('--shine-x', `50%`);
    el.style.setProperty('--shine-y', `-50%`);
  };

  return (
    <button
      ref={cardRef}
      data-testid={`task-card-${task.id}`}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="asa-card row-card task-tilt-card"
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px 14px 12px', textAlign: 'left', width: '100%',
        cursor: 'pointer',
        transition: 'transform 0.16s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s',
        transformStyle: 'preserve-3d',
        position: 'relative',
        // Iter62u/v — Differentiate video vs classic tasks visually:
        //   Video   → soft dusty-rose accents (chill, not vivid)
        //   Classic → gold accents (radial gold glow + gold thin sheen + gold border tint)
        background: isDone
          ? 'linear-gradient(160deg, rgba(38,48,90,0.55) 0%, rgba(22,32,64,0.75) 100%)'
          : isVideo
            ? 'radial-gradient(120% 100% at 100% 0%, rgba(184,92,92,0.10) 0%, transparent 55%),' +
              ' linear-gradient(160deg, rgba(46,38,52,0.85) 0%, rgba(30,24,36,0.95) 100%)'
            : 'radial-gradient(120% 100% at 100% 0%, rgba(245,166,35,0.12) 0%, rgba(245,166,35,0) 55%),' +
              ' linear-gradient(160deg, rgba(38,48,90,0.85) 0%, rgba(22,32,64,0.95) 100%)',
        border: isVideo
          ? '1.5px solid rgba(184,92,92,0.22)'
          : '1.5px solid rgba(245,166,35,0.20)',
        borderLeft: isDone
          ? '4px solid rgba(52,211,153,0.55)'
          : (isVideo ? '4px solid #B85C5C' : `4px solid ${p.color}`),
        boxShadow: !isDone && task.priority === 'high'
          ? `0 0 14px -2px ${p.color}55, 0 14px 30px -14px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -20px 40px -20px ${isVideo ? 'rgba(184,92,92,0.10)' : 'rgba(245,166,35,0.10)'}`
          : `0 12px 26px -14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -18px 36px -20px ${isVideo ? 'rgba(184,92,92,0.08)' : 'rgba(245,166,35,0.08)'}`,
        opacity: isDragging ? 0.4 : (isDone ? 0.65 : 1),
        overflow: 'hidden',
        '--shine-x': '50%',
        '--shine-y': '-50%',
      }}
    >
      {/* Iter62v — Metallic top sheen: soft dusty-rose for video / gold for classic */}
      <span style={{
        position: 'absolute', top: 0, left: 4, right: 4, height: 1,
        background: isVideo
          ? 'linear-gradient(90deg, transparent, rgba(184,92,92,0.45), transparent)'
          : 'linear-gradient(90deg, transparent, rgba(245,166,35,0.55), transparent)',
        pointerEvents: 'none',
      }} />
      {/* Iter47d — Drag handle (founder only, only for reorderable lists) */}
      {dragHandleProps && (
        <div
          {...dragHandleProps}
          onClick={(e) => e.stopPropagation()}
          title="Réordonner (glisser-déposer)"
          style={{
            width: 20, height: 30, display: 'grid', placeItems: 'center',
            color: 'var(--t3)', cursor: 'grab', flexShrink: 0, opacity: 0.5,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
        >
          <GripVertical size={16} />
        </div>
      )}

      {/* Index circle */}
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: isVideo && !isDone ? 'rgba(239,68,68,0.16)' : 'var(--sf2)',
        border: `1.5px solid ${isVideo && !isDone ? '#EF444466' : s.color + '66'}`,
        display: 'grid', placeItems: 'center',
        flexShrink: 0,
        fontFamily: 'Sora', fontWeight: 800, fontSize: '0.72rem',
        color: isVideo && !isDone ? '#EF4444' : s.color,
      }}>
        {isDone ? <Check size={16} color="#34D399" strokeWidth={2.6} /> : `#${String(idx).padStart(2, '0')}`}
      </div>

      {/* Main column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          {/* Iter47j — Video badge (no emoji, cleaner) */}
          {isVideo && !isDone && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 8px', borderRadius: 999,
              background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.45)',
              color: '#EF4444', fontFamily: 'Sora', fontWeight: 800, fontSize: '0.62rem',
              letterSpacing: '0.05em', textTransform: 'uppercase',
            }}>Vidéo</span>
          )}
          {/* Iter47j — task_type badge (Delivery / Expansion) : couleur = priorité, plus subtle, no emoji */}
          {task.task_type && !isDone && (
            <span
              data-testid={`task-type-badge-${task.id}`}
              style={{
                padding: '2px 7px', borderRadius: 999,
                background: `${p.color}15`,
                border: `1px solid ${p.color}44`,
                color: p.color,
                fontFamily: 'Sora', fontWeight: 700, fontSize: '0.58rem',
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}
            >
              {task.task_type === 'delivery' ? 'Delivery' : 'Expansion'}
            </span>
          )}
          <div className="asa-heading" style={{
            fontSize: '0.96rem', margin: 0,
            color: isDone ? 'var(--t3)' : 'var(--t1)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            textDecoration: isDone ? 'line-through' : 'none',
            flex: 1, minWidth: 0,
          }}>
            {task.display_title || task.title}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Iter47x — Category tag pill + time slot */}
          {task.category_tag && <TagPill tag={task.category_tag} size="sm" />}
          {(task.start_time || task.end_time) && <TimeSlot start={task.start_time} end={task.end_time} />}
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontFamily: 'Sora', fontWeight: 600, fontSize: '0.7rem', color: s.color,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.color }} />
            {s.label}
          </span>
          {/* Iter61w — Status pill: "Pas commencé", "Commencé"/chrono, or final duration */}
          <TaskChronoBadge task={task} />
          {isDone && doneMinutesLeft !== null && (
            <>
              <span style={{ width: 1, height: 9, background: 'var(--bd)' }} />
              <span
                data-testid={`task-countdown-${task.id}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 7px', borderRadius: 999,
                  background: 'rgba(52,211,153,0.14)', border: '1px solid rgba(52,211,153,0.35)',
                  color: '#34D399',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem',
                }}
              >
                <Archive size={9} />
                {doneMinutesLeft > 0 ? `archivage dans ${doneMinutesLeft} min` : 'archivage imminent'}
              </span>
            </>
          )}
          {/* Iter47j — Priority chip HIDDEN on video rows (already visually red) — user's request */}
          {!isDone && !isVideo && (
            <>
              <span style={{ width: 1, height: 9, background: 'var(--bd)' }} />
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontFamily: 'Lexend', fontSize: '0.72rem', color: p.color,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: p.color }} />
                {p.label}
              </span>
            </>
          )}
          {due && !isDone && (
            <>
              <span style={{ width: 1, height: 9, background: 'var(--bd)' }} />
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontFamily: 'Lexend', fontSize: '0.72rem',
                color: overdue ? 'var(--r)' : 'var(--t3)',
              }}>
                {overdue && <AlertTriangle size={11} />}
                <Clock size={11} />
                {due.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Assignee */}
      {assignee && <Avatar user={assignee} size={32} />}

      {/* Iter 62cu — Œil "Masquer pour aujourd'hui" (Hub Tâches uniquement) */}
      <HideTodayEye task={task} />

      {/* Iter47x — 3-dots menu (Modifier / Supprimer) — replaces the pencil */}
      <TaskCardMenu task={task} onEdit={onEdit} />
    </button>
  );
}

// -----------------------------------------------------------------------------
// Iter 62cu — Petit bouton œil qui toggle `hidden_on` sur la tâche. Un click →
// disparaît du Hub Tâches pour aujourd'hui uniquement (reset auto à minuit).
function HideTodayEye({ task }) {
  const [busy, setBusy] = useState(false);
  const now = new Date();
  const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isHiddenToday = task.hidden_on === todayISO;
  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.id}`, { hidden_on: isHiddenToday ? null : todayISO });
      toast.success(isHiddenToday ? 'Réaffichée dans le Hub' : 'Masquée pour aujourd\'hui');
      window.dispatchEvent(new CustomEvent('asa-task-updated'));
    } catch (err) {
      toast.error('Erreur');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      data-testid={`task-hide-today-${task.id}`}
      onClick={toggle}
      disabled={busy}
      title={isHiddenToday ? 'Réafficher aujourd\'hui' : 'Masquer du Hub pour aujourd\'hui'}
      aria-label={isHiddenToday ? 'Réafficher' : 'Masquer aujourd\'hui'}
      style={{
        width: 28, height: 28, borderRadius: 7,
        display: 'grid', placeItems: 'center',
        border: `1px solid ${isHiddenToday ? 'rgba(245,197,106,0.35)' : 'transparent'}`,
        background: isHiddenToday ? 'rgba(245,197,106,0.12)' : 'transparent',
        color: isHiddenToday ? '#F5C56A' : 'var(--t3)',
        cursor: busy ? 'wait' : 'pointer',
        transition: 'all 0.14s var(--ease-out)',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        if (!isHiddenToday) e.currentTarget.style.background = 'var(--sf2)';
      }}
      onMouseLeave={(e) => {
        if (!isHiddenToday) e.currentTarget.style.background = 'transparent';
      }}
    >
      {isHiddenToday ? <EyeOff size={13} /> : <Eye size={13} />}
    </button>
  );
}

// Iter47x — Tiny 3-dots menu shown at the end of each TaskRow
// Iter 62bq — dropdown rendered via React portal (position: fixed) to escape
// the local stacking context created by draggable/transformed task rows.
// Without this, the menu was rendered BEHIND adjacent task bars.
function TaskCardMenu({ task, onEdit }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // { top, right }
  const triggerRef = useRef(null);
  const remove = async () => {
    setOpen(false);
    if (!window.confirm(`Supprimer "${task.title}" ?\nCette action est irréversible.`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success('Tâche supprimée');
      window.dispatchEvent(new CustomEvent('asa-task-updated'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const toggle = (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) {
      // Iter 62bu — flip menu upwards if not enough room below (menu is ~90px tall).
      const MENU_H = 90;
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < MENU_H + 12 && r.top > MENU_H + 12;
      setMenuPos({
        top: openUp ? r.top - MENU_H - 6 : r.bottom + 6,
        right: window.innerWidth - r.right,
      });
    }
    setOpen(true);
  };
  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) {
        const MENU_H = 90;
        const spaceBelow = window.innerHeight - r.bottom;
        const openUp = spaceBelow < MENU_H + 12 && r.top > MENU_H + 12;
        setMenuPos({
          top: openUp ? r.top - MENU_H - 6 : r.bottom + 6,
          right: window.innerWidth - r.right,
        });
      }
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);
  return (
    <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
      <span
        ref={triggerRef}
        data-testid={`task-edit-btn-${task.id}`}
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter') toggle(e); }}
        title="Options"
        style={{
          width: 30, height: 30, borderRadius: 8,
          display: 'grid', placeItems: 'center',
          background: 'var(--sf2)', border: '1.5px solid var(--bd)',
          cursor: 'pointer', transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(91,190,252,0.12)';
          e.currentTarget.style.borderColor = 'rgba(91,190,252,0.45)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--sf2)';
          e.currentTarget.style.borderColor = 'var(--bd)';
        }}
      >
        <MoreVertical size={13} color="var(--t3)" />
      </span>
      {open && menuPos && createPortal(
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
          />
          <div
            style={{
              position: 'fixed',
              top: menuPos.top, right: menuPos.right,
              zIndex: 9999,
              background: 'var(--sf)', border: '1.5px solid var(--bd)',
              borderRadius: 9, padding: 4, minWidth: 150,
              boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
            }}
          >
            <button
              data-testid={`task-menu-edit-${task.id}`}
              onClick={() => { setOpen(false); onEdit?.(task); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 10px', borderRadius: 6,
                background: 'transparent', border: 'none', color: 'var(--t1)',
                fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
                cursor: 'pointer', textAlign: 'left',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sf2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            ><Pencil size={12} /> Modifier</button>
            <button
              data-testid={`task-menu-delete-${task.id}`}
              onClick={remove}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 10px', borderRadius: 6,
                background: 'transparent', border: 'none', color: '#FF6B6B',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                cursor: 'pointer', textAlign: 'left',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,107,107,0.10)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            ><Trash2 size={12} /> Supprimer</button>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

// Iter47d — Sortable wrapper (founder drag-and-drop). Falls back to plain TaskRow when disabled.
function SortableTaskRow({ id, idx, task, users, onClick, onEdit }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style}>
      <TaskRow
        idx={idx}
        task={task}
        users={users}
        onClick={onClick}
        onEdit={onEdit}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragging={isDragging}
      />
    </div>
  );
}


// Iter47d — Renders a list of tasks with founder-only drag-and-drop.
function TaskListDnd({ tasks, enabled, users, onClick, onEdit, onReordered, startIdx = 0 }) {
  const [orderedIds, setOrderedIds] = useState(tasks.map((t) => t.id));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Keep local order in sync when parent tasks list changes
  useEffect(() => {
    setOrderedIds(tasks.map((t) => t.id));
  }, [tasks]);

  if (!enabled) {
    return (
      <>
        {tasks.map((t, idx) => (
          <TaskRow
            key={t.id}
            idx={startIdx + idx + 1}
            task={t}
            users={users}
            onClick={() => onClick(t)}
            onEdit={onEdit}
          />
        ))}
      </>
    );
  }

  const byId = new Map(tasks.map((t) => [t.id, t]));
  const ordered = orderedIds.map((id) => byId.get(id)).filter(Boolean);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(active.id);
    const newIndex = orderedIds.indexOf(over.id);
    const next = arrayMove(orderedIds, oldIndex, newIndex);
    setOrderedIds(next);
    onReordered && onReordered(next);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
        {ordered.map((t, idx) => (
          <SortableTaskRow
            key={t.id}
            id={t.id}
            idx={startIdx + idx + 1}
            task={t}
            users={users}
            onClick={() => onClick(t)}
            onEdit={onEdit}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}


// Iter47i — Section header used across the tasks list for a clean, hierarchised layout.
// Minimalist bar : accent stripe + label + count chip. No emoji, no noise.
function SectionHeader({ label, count, accent = '#5BBEFC', icon: IconComp }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '0 4px 10px',
        marginBottom: 4,
        borderBottom: '1.5px solid var(--bd)',
      }}
    >
      <span
        style={{
          width: 3, height: 16, borderRadius: 2, background: accent,
        }}
      />
      {IconComp && <IconComp size={13} color={accent} strokeWidth={2.2} />}
      <span
        style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: '0.86rem',
          color: 'var(--t1)',
          textTransform: 'uppercase', letterSpacing: '0.03em',
        }}
      >
        {label}
      </span>
      {typeof count === 'number' && (
        <span
          style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem',
            padding: '1.5px 8px', borderRadius: 999,
            background: `${accent}1F`, color: accent,
            letterSpacing: '0.05em',
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
}


// Iter47j — Compact dropdown button used across the tasks toolbar.
// Renders as: [Label · Value ▾] with a menu of options.
function ToolbarDropdown({ testId, label, value, options, onSelect, current }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 10,
          background: open ? 'var(--sf2)' : 'var(--sf)',
          border: `1.5px solid ${open ? 'var(--bdh)' : 'var(--bd)'}`,
          color: 'var(--t1)',
          fontFamily: 'Sora', fontWeight: 700, fontSize: '0.78rem',
          cursor: 'pointer', transition: 'all 0.15s var(--ease-out)',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ color: 'var(--t3)', fontWeight: 600 }}>{label}</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>{value}</span>
        <ChevronDown size={12} color="var(--t3)" strokeWidth={2.2} style={{
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
        }} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div
            data-testid={`${testId}-menu`}
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 41,
              minWidth: 180, padding: 5,
              background: 'var(--sf)', border: '1.5px solid var(--bd)',
              borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            }}
          >
            {options.map((o) => {
              const active = current === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  data-testid={`${testId}-option-${o.id}`}
                  onClick={() => { onSelect(o.id); setOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '9px 10px', borderRadius: 8,
                    background: active ? 'rgba(46,163,242,0.10)' : 'transparent',
                    border: 'none', textAlign: 'left', cursor: 'pointer',
                    color: active ? 'var(--pl)' : 'var(--t1)',
                    fontFamily: 'Sora', fontWeight: active ? 700 : 600, fontSize: '0.82rem',
                    transition: 'all 0.12s',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--sf2)'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  {active && <Check size={12} strokeWidth={2.4} />}
                  <span style={{ marginLeft: active ? 0 : 20 }}>{o.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Iter47p — AssigneeRolePicker : 3 pills (Monteur / Founder / Client) + filtered <select>
// UX rationale: previous flat dropdown listing ALL users was hard to scan.
// Now the user first picks a role, then only that role's users appear.
// ---------------------------------------------------------------------------
function AssigneeRolePicker({ value, onChange, users, testidPrefix = 'assignee', restrictToRoles = null }) {
  const roleOf = (uid) => users.find((u) => u.id === uid)?.role || null;
  const allTabs = [
    { key: 'contributor', label: 'Monteur' },
    { key: 'founder', label: 'Founder' },
    { key: 'client', label: 'Client' },
  ];
  const ROLE_TABS = restrictToRoles ? allTabs.filter((t) => restrictToRoles.includes(t.key)) : allTabs;
  const defaultRole = ROLE_TABS[0]?.key || 'contributor';
  const initialRole = value ? (roleOf(value) || defaultRole) : defaultRole;
  const [role, setRole] = useState(initialRole);
  const filtered = (users || []).filter((u) => u.role === role && !u.is_archived);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        {ROLE_TABS.map((r) => {
          const active = role === r.key;
          return (
            <button
              key={r.key}
              type="button"
              data-testid={`${testidPrefix}-role-${r.key}`}
              onClick={() => {
                setRole(r.key);
                // If current value is no longer in the role's list, clear it
                if (value && roleOf(value) !== r.key) onChange('');
              }}
              style={{
                flex: 1,
                padding: '7px 10px', borderRadius: 8,
                background: active ? 'rgba(91,190,252,0.14)' : 'var(--sf2)',
                border: `1.5px solid ${active ? 'rgba(91,190,252,0.55)' : 'var(--bd)'}`,
                color: active ? '#5BBEFC' : 'var(--t2)',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                letterSpacing: '0.01em',
                cursor: 'pointer',
                transition: 'all 0.12s',
              }}
            >{r.label}</button>
          );
        })}
      </div>
      <select
        data-testid={`${testidPrefix}-select`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', padding: '10px 12px',
          background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 10,
          fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)',
        }}
      >
        <option value="">— aucun —</option>
        {filtered.map((u) => (
          <option key={u.id} value={u.id}>{u.name}</option>
        ))}
        {filtered.length === 0 && <option disabled>Aucun {role === 'contributor' ? 'monteur' : role}</option>}
      </select>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Iter43h — Quick edit modal (works for ANY task type + any stage, incl done)
// ---------------------------------------------------------------------------
function QuickTaskEditModal({ task, users, onClose, onSaved, onSilentUpdate }) {
  const { user: currentUser } = useAuth();
  const isClient = currentUser?.role === 'client';
  const restrictToRoles = isClient ? ['founder'] : null;
  // Iter61r — keep a local copy of the task so Frame.io actions reflect immediately
  const [localTask, setLocalTask] = useState(task);
  const [form, setForm] = useState({
    title: task.title || '',
    description: task.description || '',
    priority: task.priority || 'medium',
    stage: task.stage || 'todo',
    assigned_to: task.assigned_to || '',
    assigned_to_montage: task.assigned_to_montage || '',
    assigned_to_derush: task.assigned_to_derush || '',
    due_date: (task.due_date || '').slice(0, 10),
    video_format: task.video_format || '',
    task_type: task.task_type || '',
    category_tag: task.category_tag || '',   // Iter47x
    start_time: task.start_time || '',
    end_time: task.end_time || '',
    drive_link: task.drive_link || '',       // Iter47x — editable inline
  });
  const [busy, setBusy] = useState(false);
  const isVideo = task.kind === 'video';

  // Iter47x — Auto-fill time slots when the tag changes
  const setCategoryTag = (tagId) => {
    const t = CATEGORY_TAGS[tagId];
    if (!t) { setForm((f) => ({ ...f, category_tag: '' })); return; }
    setForm((f) => ({
      ...f,
      category_tag: tagId,
      start_time: f.start_time || t.defaultStart,
      end_time: f.end_time || t.defaultEnd,
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const patch = { ...form, due_date: form.due_date || null };
      // Iter47d — task_type: empty string means "unset"
      patch.task_type = form.task_type || null;
      // Iter47x — empty strings → null
      patch.category_tag = form.category_tag || null;
      patch.start_time = form.start_time || null;
      patch.end_time = form.end_time || null;
      patch.drive_link = form.drive_link || null;
      if (!isVideo) {
        delete patch.assigned_to_montage;
        delete patch.assigned_to_derush;
        delete patch.video_format;
      }
      const r = await api.patch(`/tasks/${task.id}`, patch);
      toast.success('Tâche modifiée');
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,26,51,0.65)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="quick-task-edit-modal"
        className="scale-in"
        style={{
          width: '100%', maxWidth: 560,
          background: 'var(--sf)', borderRadius: 16,
          border: '1.5px solid var(--bd)',
          padding: '22px 22px 20px',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(245,158,11,0.14)', display: 'grid', placeItems: 'center' }}>
            <Pencil size={16} color="#F59E0B" />
          </div>
          <div>
            <div className="asa-eyebrow" style={{ color: '#F59E0B' }}>Modifier</div>
            <h2 className="asa-heading" style={{ fontSize: 15, margin: 0 }}>La tâche</h2>
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} data-testid="quick-edit-close" style={{ padding: 6, borderRadius: 8, cursor: 'pointer' }}>
            <X size={16} color="var(--t3)" />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <QEField label="Titre">
            <input
              data-testid="quick-edit-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              style={{ width: '100%' }}
            />
          </QEField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <QEField label="Priorité">
              <select
                data-testid="quick-edit-priority"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                style={{ width: '100%' }}
              >
                <option value="high">🔴 Haute</option>
                <option value="medium">🟡 Moyenne</option>
                <option value="low">🟢 Basse</option>
              </select>
            </QEField>
            <QEField label="Statut">
              <select
                data-testid="quick-edit-stage"
                value={form.stage}
                onChange={(e) => setForm({ ...form, stage: e.target.value })}
                style={{ width: '100%' }}
              >
                <option value="todo">À faire</option>
                <option value="in_progress">En cours</option>
                <option value="review">À valider</option>
                <option value="done">Terminé</option>
              </select>
            </QEField>
          </div>
          {/* Iter47j — Task type (Delivery / Expansion) : compact, no emoji */}
          <QEField label="Type de tâche">
            <div style={{ display: 'flex', gap: 6 }}>
              <TypeBtn
                testid="quick-edit-type-delivery"
                active={form.task_type === 'delivery'}
                onClick={() => setForm({ ...form, task_type: form.task_type === 'delivery' ? '' : 'delivery' })}
                color="#34D399"
                label="Delivery"
              />
              <TypeBtn
                testid="quick-edit-type-expansion"
                active={form.task_type === 'expansion'}
                onClick={() => setForm({ ...form, task_type: form.task_type === 'expansion' ? '' : 'expansion' })}
                color="#A78BFA"
                label="Expansion"
              />
            </div>
          </QEField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <QEField label="Deadline">
              <input
                data-testid="quick-edit-due"
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                style={{ width: '100%' }}
              />
            </QEField>
            {!isVideo && (
              <QEField label="Assigné à">
                <AssigneeRolePicker
                  value={form.assigned_to}
                  onChange={(v) => setForm({ ...form, assigned_to: v })}
                  users={users}
                  testidPrefix="quick-edit-assignee"
                  restrictToRoles={restrictToRoles}
                />
              </QEField>
            )}
          </div>

          {/* Iter47x — Catégorie + créneau horaire + Drive link inline */}
          <QEField label="Catégorie">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 5 }}>
              {CATEGORY_TAG_ORDER.map((k) => {
                const t = CATEGORY_TAGS[k];
                const active = form.category_tag === k;
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`quick-edit-tag-${k}`}
                    onClick={() => setCategoryTag(active ? null : k)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '7px 10px', borderRadius: 8,
                      background: active ? t.bg : 'var(--sf2)',
                      border: `1.5px solid ${active ? t.border : 'var(--bd)'}`,
                      color: active ? t.color : 'var(--t2)',
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                    {t.short}
                  </button>
                );
              })}
            </div>
          </QEField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <QEField label="Début">
              <input
                type="time"
                data-testid="quick-edit-start-time"
                value={form.start_time || ''}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                style={{ width: '100%' }}
              />
            </QEField>
            <QEField label="Fin">
              <input
                type="time"
                data-testid="quick-edit-end-time"
                value={form.end_time || ''}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                style={{ width: '100%' }}
              />
            </QEField>
          </div>
          <QEField label="Lien Drive (rush)">
            <input
              type="url"
              data-testid="quick-edit-drive-link"
              placeholder="https://drive.google.com/…"
              value={form.drive_link || ''}
              onChange={(e) => setForm({ ...form, drive_link: e.target.value })}
              style={{ width: '100%' }}
            />
          </QEField>

          {/* Iter61r bis — Frame.io: shown only if enabled at task creation.
              Founder still has a small toggle to enable/disable retroactively. */}
          {currentUser?.role === 'founder' && (
            <FrameioToggle
              enabled={!!localTask.frameio_enabled}
              onToggle={async (enabled) => {
                try {
                  const r = await api.patch(`/tasks/${localTask.id}`, { frameio_enabled: enabled });
                  setLocalTask(r.data);
                  onSilentUpdate?.(r.data);
                  toast.success(enabled ? 'Frame.io activé' : 'Frame.io désactivé');
                } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
              }}
            />
          )}
          {localTask.frameio_enabled && (
            <div id="frameio-focus-anchor">
              <FrameioReviewSection
                task={localTask}
                users={users}
                onUpdated={(updated) => {
                  if (updated) {
                    setLocalTask(updated);
                    onSilentUpdate?.(updated);
                  }
                }}
              />
            </div>
          )}
          {isVideo && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <QEField label="Monteur">
                <select
                  data-testid="quick-edit-monteur"
                  value={form.assigned_to_montage}
                  onChange={(e) => setForm({ ...form, assigned_to_montage: e.target.value })}
                  style={{ width: '100%' }}
                >
                  <option value="">— aucun —</option>
                  {(users || []).filter((u) => u.role !== 'founder').map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </QEField>
              <QEField label="Dérusheur">
                <select
                  data-testid="quick-edit-derusheur"
                  value={form.assigned_to_derush}
                  onChange={(e) => setForm({ ...form, assigned_to_derush: e.target.value })}
                  style={{ width: '100%' }}
                >
                  <option value="">— aucun —</option>
                  {(users || []).filter((u) => u.role !== 'founder').map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </QEField>
            </div>
          )}
          {isVideo && (
            <QEField label="Format vidéo">
              <select
                data-testid="quick-edit-format"
                value={form.video_format}
                onChange={(e) => setForm({ ...form, video_format: e.target.value })}
                style={{ width: '100%' }}
              >
                <option value="podcast">Podcast</option>
                <option value="facecam">Facecam</option>
                <option value="short">Short</option>
                <option value="special">Spécial</option>
              </select>
            </QEField>
          )}
          <QEField label="Description">
            <textarea
              data-testid="quick-edit-description"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </QEField>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', fontSize: 12, color: 'var(--t3)', cursor: 'pointer' }}>Annuler</button>
          <button
            data-testid="quick-edit-save"
            onClick={save}
            disabled={busy}
            className="asa-btn"
            style={{ padding: '8px 16px', fontSize: 12 }}
          >
            {busy ? <Loader2 size={13} className="spin" /> : <Check size={13} />}
            {busy ? '…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function QEField({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
      <div className="asa-field" style={{ padding: '8px 12px' }}>{children}</div>
    </label>
  );
}

// Iter47j — Type toggle button (Delivery / Expansion) — compact, no emoji
function TypeBtn({ testid, active, onClick, color, label }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: '7px 10px', borderRadius: 8,
        background: active ? `${color}22` : 'var(--sf2)',
        border: '1.5px solid ' + (active ? `${color}88` : 'var(--bd)'),
        color: active ? color : 'var(--t2)',
        fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
        letterSpacing: '0.02em', textTransform: 'uppercase',
        cursor: 'pointer', transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}


// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------
function EmptyState({ search, filter, onCreate }) {
  const label =
    search ? 'Aucun résultat'
    : filter === 'all' ? 'Aucune tâche'
    : 'Rien dans cette colonne';
  return (
    <div style={{
      textAlign: 'center', padding: '50px 20px 30px',
      color: 'var(--t3)',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        background: 'var(--sf)', border: '1.5px solid var(--bd)',
        margin: '0 auto 16px',
        display: 'grid', placeItems: 'center',
      }}>
        <Check size={24} color="var(--t3)" strokeWidth={1.5} />
      </div>
      <div className="t-display-sm" style={{ color: 'var(--t2)' }}>{label}</div>
      <div className="t-body-md" style={{ color: 'var(--t3)', marginTop: 6, marginBottom: 18 }}>
        {search ? 'Essaie un autre mot-clé' : 'Crée une nouvelle tâche pour commencer'}
      </div>
      {!search && (
        <button onClick={onCreate} className="asa-btn" style={{ margin: '0 auto' }}>
          <Plus size={15} /> Nouvelle tâche
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right-slide detail sidebar (port of ASA iOS detail sheet)
// ---------------------------------------------------------------------------
export function DetailSidebar({ task, users, isFounder, onClose, onSaved, onDelete }) {
  const { user } = useAuth();
  // Iter61r bis — local task copy so FrameioReviewSection updates inline
  const [localTask, setLocalTask] = useState(task);
  useEffect(() => { setLocalTask(task); }, [task]);
  // Determine if this task is a video/montage task (4-block layout) vs a normal task (simple checkbox)
  const tags = Array.isArray(task.tags) ? task.tags : [];
  const isVideoTask = (
    task.kind === 'video' ||
    tags.includes('video_deliverable') ||
    task.event_type === 'content' ||
    !!task.concept_id
  );
  const [form, setForm] = useState({
    title: task.title,
    description: task.description || '',
    stage: task.stage,
    priority: task.priority,
    assigned_to: task.assigned_to,
    due_date: task.due_date ? task.due_date.slice(0, 10) : '',
    concept_id: task.concept_id || null,
    tech_instructions: task.tech_instructions || '',
    inspiration_link: task.inspiration_link || '',
    consignes_validated: !!task.consignes_validated,
    concept_validated: !!task.concept_validated,
    tech_validated: !!task.tech_validated,
    publication_done: !!task.publication_done,
    monteur_finalized: !!task.monteur_finalized,
  });
  const [busy, setBusy] = useState(false);
  const [concepts, setConcepts] = useState([]);
  const [formatSettings, setFormatSettings] = useState([]);
  const [project, setProject] = useState(null);
  const [showConceptPicker, setShowConceptPicker] = useState(false);

  useEffect(() => {
    const params = task.project_id ? `?project_id=${task.project_id}` : '';
    Promise.all([
      api.get(`/concepts${params}`).then((r) => r.data).catch(() => []),
      api.get('/format-settings').then((r) => r.data).catch(() => []),
      task.project_id
        ? api.get(`/projects/${task.project_id}`).then((r) => r.data).catch(() => null)
        : Promise.resolve(null),
    ]).then(([c, fs, p]) => { setConcepts(c); setFormatSettings(fs); setProject(p); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  const patchTask = async (patch) => {
    try { await api.patch(`/tasks/${task.id}`, patch); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.id}`, { ...form, due_date: form.due_date || null });
      toast.success('Tâche mise à jour');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    try { await api.delete(`/tasks/${task.id}`); toast.success('Supprimée'); onDelete(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const setStageQuick = async (newStage) => {
    setForm({ ...form, stage: newStage });
    try {
      const r = await api.patch(`/tasks/${task.id}`, { stage: newStage });
      const finalStage = r?.data?.stage || newStage;
      if (finalStage !== newStage) setForm((f) => ({ ...f, stage: finalStage }));
      toast.success(
        finalStage === 'to_post'
          ? 'Vidéo terminée ✓ — envoyée au posteur (À poster)'
          : finalStage === 'done'
            ? 'Tâche terminée — archivée auto dans 5 min'
            : `Statut : ${STAGES[finalStage]?.label || finalStage}`
      );
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const concept = concepts.find((c) => c.id === form.concept_id);
  const drive_link_for_format = concept
    ? (formatSettings.find((s) => s.format === concept.format)?.drive_link || null)
    : null;

  // ---- Bloc unlocking (the user wants strict ordering) ----
  const block1Active = true;
  const block2Active = form.consignes_validated;
  const block3Active = form.tech_validated; // concept picker + finalize after tech validated
  const block4Active = form.concept_validated || form.monteur_finalized; // publication unlocks after monteur finalizes

  const monteurMarkFinalized = async () => {
    const next = !form.monteur_finalized;
    setForm({ ...form, monteur_finalized: next });
    try {
      await api.patch(`/tasks/${task.id}`, { monteur_finalized: next });
      toast.success(next ? 'Tâche finalisée — Rayan & Hugo reçoivent la notification' : 'Finalisation annulée');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 90,
        background: 'rgba(10, 26, 51, 0.55)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="page-fade"
        data-testid="task-detail-sidebar"
        style={{
          width: '100%',
          maxWidth: task.kind === 'video' ? 1280 : 480,
          background: 'var(--sf)',
          borderLeft: '1.5px solid var(--bd)',
          overflowY: 'auto',
          padding: '22px 22px 30px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <button onClick={onClose} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            color: 'var(--p)', fontFamily: 'Sora', fontWeight: 600, fontSize: '0.85rem',
            padding: 6, borderRadius: 8,
          }}>
            <ArrowLeft size={16} /> Fermer
          </button>
          <div style={{ flex: 1 }} />
          {isFounder && isVideoTask && task.stage !== 'done' && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 10px', borderRadius: 999, background: 'rgba(245,197,106,0.12)', border: '1px solid rgba(245,197,106,0.4)', fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: '#F5C56A' }}>
              📱 Poster
              <select
                data-testid="detail-poster-select"
                value={localTask.assigned_to_poster || project?.poster_id || ''}
                onChange={async (e) => {
                  const id = e.target.value;
                  if (!id) return;
                  try {
                    await api.patch(`/tasks/${task.id}`, { assigned_to_poster: id });
                    setLocalTask((t) => ({ ...t, assigned_to_poster: id }));
                    toast.success(`Poster → ${users.find((u) => u.id === id)?.name || id}`);
                  } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
                }}
                style={{ background: 'transparent', border: 'none', color: '#F5C56A', fontFamily: 'Sora', fontWeight: 700, fontSize: 11, cursor: 'pointer', outline: 'none' }}
              >
                <option value="" style={{ color: '#0B1330' }}>Aucun</option>
                {users.filter((u) => u.role !== 'client' && !u.is_archived).map((u) => <option key={u.id} value={u.id} style={{ color: '#0B1330' }}>{u.name}</option>)}
              </select>
            </label>
          )}
          {form.monteur_finalized && (
            <span
              data-testid="monteur-finalized-badge"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 999,
                background: 'rgba(52,211,153,0.18)', border: '1.5px solid rgba(52,211,153,0.45)',
                color: '#34D399', fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}
            ><Check size={11} /> Finalisée</span>
          )}
          {isFounder && (
            <button
              data-testid="task-delete-btn"
              onClick={remove}
              style={{
                padding: 8, borderRadius: 8,
                background: 'rgba(255,107,107,0.08)',
                border: '1px solid rgba(255,107,107,0.2)',
              }}
            >
              <Trash2 size={14} color="var(--r)" />
            </button>
          )}
        </div>

        {/* Title */}
        <input
          data-testid="task-title-input"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          onBlur={() => isFounder && patchTask({ title: form.title })}
          readOnly={!isFounder}
          className="asa-display"
          style={{
            width: '100%', fontSize: 'var(--text-xl)', color: 'var(--t1)',
            padding: '6px 0', marginBottom: 6,
            textDecoration: (form.stage === 'done' || form.monteur_finalized) ? 'line-through' : 'none',
            opacity: (form.stage === 'done' || form.monteur_finalized) ? 0.55 : 1,
          }}
        />

        {/* Iter61r bis — Frame.io shown right below title (above description).
            Toggle is founder-only. Section only renders when frameio_enabled=true.
            Iter61t — null-safe: localTask can be undefined during initial render. */}
        {isFounder && localTask && (
          <div style={{ marginTop: 10, marginBottom: 12 }}>
            <FrameioToggle
              enabled={!!localTask.frameio_enabled}
              onToggle={async (enabled) => {
                try {
                  const r = await api.patch(`/tasks/${localTask.id}`, { frameio_enabled: enabled });
                  setLocalTask(r.data);
                  onSaved?.(r.data);
                } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
              }}
            />
          </div>
        )}
        {localTask?.frameio_enabled && (
          <div id="frameio-focus-anchor" style={{ marginTop: 10, marginBottom: 14 }}>
            <FrameioReviewSection
              task={localTask}
              users={users}
              onUpdated={(updated) => { if (updated) { setLocalTask(updated); onSaved?.(updated); } }}
            />
          </div>
        )}

        {/* Iter 62fs — Livraison rapide (pas de Frame.io activé) : colle n'importe
            quel lien (Drive, YouTube, Frame, autre) + bouton "Vidéo terminée"
            → auto-passage à "À poster" (poster_id du projet pré-rempli, date +7j
            auto, webhook ASA Client déclenché avec preview). */}
        {localTask && !localTask.frameio_enabled && localTask.kind === 'video' && localTask.stage !== 'to_post' && localTask.stage !== 'done' && (
          <QuickDeliverySection
            task={localTask}
            onDelivered={(updated) => { if (updated) { setLocalTask(updated); onSaved?.(updated); } }}
          />
        )}

        {/* Status pills — monteur cannot move video tasks to 'done', but CAN do it for normal tasks (Iter43) */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14, marginBottom: 20 }}>
          {STAGE_ORDER.map((sid) => {
            const s = STAGES[sid];
            const active = form.stage === sid;
            const disabled = false;
            return (
              <button
                key={sid}
                data-testid={`stage-${sid}`}
                onClick={() => !disabled && setStageQuick(sid)}
                disabled={disabled}
                title={disabled ? 'Réservé aux founders' : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 11px',
                  background: active ? `${s.color}1F` : 'var(--sf2)',
                  border: `1.5px solid ${active ? `${s.color}66` : 'var(--bd)'}`,
                  borderRadius: 999,
                  fontFamily: 'Sora', fontWeight: 600, fontSize: '0.78rem',
                  color: active ? s.color : 'var(--t2)',
                  opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color }} />
                {s.label}
                {disabled && <Lock size={10} />}
              </button>
            );
          })}
        </div>

        {/* === Normal (non-video) task: simple description + done checkbox === */}
        {!isVideoTask && (
          <div style={{ marginBottom: 18 }}>
            <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Description</div>
            {isFounder ? (
              <textarea
                data-testid="normal-task-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                onBlur={() => patchTask({ description: form.description })}
                placeholder="Détails de la tâche…"
                rows={4}
                style={blockInputStyle()}
              />
            ) : (
              <div style={{ ...readOnlyChip(), minHeight: 60, whiteSpace: 'pre-wrap' }}>
                {form.description || '—'}
              </div>
            )}

            <button
              data-testid="normal-task-toggle-done"
              onClick={() => setStageQuick(form.stage === 'done' ? 'todo' : 'done')}
              style={{
                marginTop: 14, width: '100%', padding: '11px 14px', borderRadius: 10,
                background: form.stage === 'done' ? 'rgba(52,211,153,0.18)' : 'var(--p)',
                border: form.stage === 'done' ? '1.5px solid rgba(52,211,153,0.45)' : 'none',
                color: form.stage === 'done' ? '#34D399' : '#fff',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                cursor: 'pointer',
              }}
            >
              <Check size={14} />
              {form.stage === 'done' ? 'Tâche terminée' : 'Marquer comme terminée'}
            </button>
          </div>
        )}

        {/* === Iter31: New Phase 1/Phase 2 workflow for video tasks === */}
        {isVideoTask && task.kind === 'video' && (
          <VideoPhaseWorkflow
            task={task}
            project={project}
            currentUser={user}
            users={users}
            onChanged={onSaved}
          />
        )}

        {/* === BLOC 1 — Consignes (Titre, Client, Échéance, Format — read-only for monteur) === */}
        {isVideoTask && task.kind !== 'video' && (<>
        <ProgressBlock
          num={1}
          label="Consignes"
          active={block1Active}
          validated={form.consignes_validated}
          onValidate={isFounder ? () => { setForm((p) => ({ ...p, consignes_validated: true })); patchTask({ consignes_validated: true }); } : null}
          onUnvalidate={isFounder ? () => { setForm((p) => ({ ...p, consignes_validated: false })); patchTask({ consignes_validated: false }); } : null}
          testid="block-consignes"
          readOnlyLabel={isFounder ? null : 'Rempli par les founders'}
        >
          <BlockField label="Titre de la vidéo">
            <div style={readOnlyChip()}>{form.title || '—'}</div>
          </BlockField>
          <BlockField label="Client">
            <div style={readOnlyChip()}>{project?.client || '—'}</div>
          </BlockField>
          <BlockField label="Échéance">
            {isFounder ? (
              <input
                type="date"
                data-testid="bloc1-due-date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                onBlur={() => patchTask({ due_date: form.due_date || null })}
                style={blockInputStyle(true)}
              />
            ) : (
              <div style={readOnlyChip()}>{form.due_date ? new Date(form.due_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</div>
            )}
          </BlockField>
          <BlockField label="Format">
            <div style={readOnlyChip()}>
              {concept ? `${formatLabel(concept.format)}` : '—'}
            </div>
          </BlockField>
          {isFounder && (
            <BlockField label="Description (optionnel)">
              <textarea
                data-testid="bloc1-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                onBlur={() => patchTask({ description: form.description })}
                placeholder="Notes additionnelles pour le monteur"
                rows={2}
                style={blockInputStyle()}
              />
            </BlockField>
          )}
        </ProgressBlock>

        {/* === BLOC 2 — Consignes techniques (lien inspiration + lien drive) === */}
        <ProgressBlock
          num={2}
          label="Consignes techniques"
          active={block2Active}
          validated={form.tech_validated}
          onValidate={isFounder ? () => { setForm((p) => ({ ...p, tech_validated: true })); patchTask({ tech_validated: true }); } : null}
          onUnvalidate={isFounder ? () => { setForm((p) => ({ ...p, tech_validated: false })); patchTask({ tech_validated: false }); } : null}
          testid="block-tech"
          readOnlyLabel={isFounder ? null : 'Rempli par les founders'}
        >
          <BlockField label="Lien d'inspiration">
            {isFounder ? (
              <input
                data-testid="bloc2-inspiration"
                value={form.inspiration_link}
                onChange={(e) => setForm({ ...form, inspiration_link: e.target.value })}
                onBlur={() => patchTask({ inspiration_link: form.inspiration_link })}
                placeholder="https://…"
                style={blockInputStyle(true)}
              />
            ) : form.inspiration_link ? (
              <a href={form.inspiration_link} target="_blank" rel="noreferrer" style={inspLinkStyle('inspi')}>
                <LinkIcon size={11} /> Voir l&apos;inspiration
              </a>
            ) : (
              <div style={readOnlyChip()}>—</div>
            )}
          </BlockField>
          <BlockField label="Lien Drive / Rush">
            {isFounder ? (
              <input
                data-testid="bloc2-tech-link"
                value={form.tech_instructions}
                onChange={(e) => setForm({ ...form, tech_instructions: e.target.value })}
                onBlur={() => patchTask({ tech_instructions: form.tech_instructions })}
                placeholder="Drive / SwissTransfer / lien des rushes"
                style={blockInputStyle(true)}
              />
            ) : form.tech_instructions ? (
              <a href={form.tech_instructions} target="_blank" rel="noreferrer" style={inspLinkStyle('drive')}>
                <Folder size={11} /> Ouvrir le Drive
              </a>
            ) : (
              <div style={readOnlyChip()}>—</div>
            )}
          </BlockField>
        </ProgressBlock>

        {/* === BLOC 3 — Concept & Vidéo montée + Finalisation monteur === */}
        <ProgressBlock
          num={3}
          label="Vidéo montée"
          active={block3Active}
          validated={form.monteur_finalized}
          onValidate={null /* monteur uses dedicated button */}
          onUnvalidate={null}
          testid="block-concept"
        >
          <BlockField label={`Sélectionne la vidéo que tu as montée${project ? ` (projet : ${project.name})` : ''}`}>
            {concept ? (
              <div
                style={{
                  padding: 11, borderRadius: 10,
                  background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                  marginBottom: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 999,
                    background: `${formatColor(concept.format)}1F`,
                    color: formatColor(concept.format),
                    fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>{formatLabel(concept.format)} n°{concept.format_number}</span>
                  <button
                    onClick={() => setShowConceptPicker(true)}
                    data-testid="bloc3-change-concept"
                    style={{
                      marginLeft: 'auto', padding: '3px 8px', borderRadius: 7,
                      background: 'transparent', border: '1px solid var(--bd)',
                      color: 'var(--t3)', fontFamily: 'Sora', fontSize: 10, fontWeight: 600,
                    }}
                  >Changer</button>
                </div>
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5, color: 'var(--t1)' }}>{concept.title}</div>
                {drive_link_for_format && (
                  <a
                    href={drive_link_for_format}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="bloc3-drive-link"
                    style={{ ...inspLinkStyle('drive'), marginTop: 6 }}
                  ><Folder size={10} /> Drive {formatLabel(concept.format)}</a>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowConceptPicker(true)}
                data-testid="bloc3-pick-concept"
                style={{
                  width: '100%', padding: 12, borderRadius: 10,
                  background: 'var(--sf2)',
                  border: '1.5px dashed var(--bd)',
                  color: 'var(--t3)',
                  fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5,
                  cursor: block3Active ? 'pointer' : 'not-allowed',
                  opacity: block3Active ? 1 : 0.6,
                  marginBottom: 8,
                }}
                disabled={!block3Active}
              >
                + Choisir la vidéo
              </button>
            )}
          </BlockField>

          {/* Finalisation button — monteur side */}
          {block3Active && (
            <button
              data-testid="bloc3-finalize"
              onClick={monteurMarkFinalized}
              disabled={!form.concept_id}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 10,
                background: form.monteur_finalized ? 'rgba(52,211,153,0.18)' : 'var(--p)',
                border: form.monteur_finalized ? '1.5px solid rgba(52,211,153,0.45)' : 'none',
                color: form.monteur_finalized ? '#34D399' : '#fff',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                opacity: form.concept_id ? 1 : 0.45,
                cursor: form.concept_id ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <Check size={13} />
              {form.monteur_finalized ? 'Vidéo finalisée — en attente de validation' : 'Marquer la vidéo comme finalisée'}
            </button>
          )}
        </ProgressBlock>

        {/* === BLOC 4 — Publication (founder validates) === */}
        {isFounder ? (
          <ProgressBlock
            num={4}
            label="Publication"
            active={block4Active}
            validated={form.publication_done}
            onValidate={() => { setForm((p) => ({ ...p, publication_done: true, concept_validated: true })); patchTask({ publication_done: true, concept_validated: true, stage: 'done' }); }}
            onUnvalidate={() => { setForm((p) => ({ ...p, publication_done: false })); patchTask({ publication_done: false }); }}
            optional
            testid="block-publication"
          >
            <div style={{
              padding: 12, borderRadius: 10,
              background: 'var(--sf2)', border: '1.5px solid var(--bd)',
              fontFamily: 'Lexend', fontSize: 12, color: 'var(--t2)',
              lineHeight: 1.55,
            }}>
              {form.monteur_finalized
                ? "Le monteur a finalisé. Valide pour marquer définitivement la tâche terminée."
                : "Ce bloc se débloquera quand le monteur aura finalisé sa vidéo."}
            </div>
          </ProgressBlock>
        ) : (
          // Monteurs see a locked placeholder
          <div
            data-testid="block-publication-locked"
            style={{
              padding: '12px 16px', marginBottom: 14, borderRadius: 14,
              background: 'rgba(58,74,102,0.10)',
              border: '1.5px solid rgba(58,74,102,0.35)',
              opacity: 0.55,
              display: 'flex', alignItems: 'center', gap: 10,
            }}
          >
            <Lock size={14} color="var(--t3)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t3)' }}>
                Publication
              </div>
              <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
                Validation réservée aux founders
              </div>
            </div>
          </div>
        )}
        </>)}

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button
            onClick={onClose}
            className="asa-btn-ghost asa-btn"
            style={{ flex: 1 }}
          >Fermer</button>
          {isFounder && (
            <button
              data-testid="task-save-btn"
              onClick={save}
              disabled={busy}
              className="asa-btn"
              style={{ flex: 1 }}
            >{busy ? <Loader2 size={15} className="spin" /> : 'Tout enregistrer'}</button>
          )}
        </div>

        {showConceptPicker && (
          <ConceptPicker
            concepts={concepts}
            current={form.concept_id}
            onClose={() => setShowConceptPicker(false)}
            onPick={(cid) => {
              setForm({ ...form, concept_id: cid });
              patchTask({ concept_id: cid });
              setShowConceptPicker(false);
            }}
            onCreated={(c) => { setConcepts((p) => [...p, c]); }}
            allowCreate={isFounder}
            projectId={task.project_id}
          />
        )}

        {/* Iter61w — Chrono bar (start button for assignee, running timer for founders, final duration when done) */}
        <TaskChronoBar
          task={localTask || task}
          currentUserId={user?.id}
          isFounder={isFounder}
          onChanged={(updated) => { if (updated) { setLocalTask(updated); onSaved?.(updated); } }}
        />
      </aside>
    </div>
  );
}

// Read-only display chip
function readOnlyChip() {
  return {
    width: '100%', padding: '10px 12px', borderRadius: 10,
    background: 'var(--sf2)', border: '1.5px solid var(--bd)',
    fontFamily: 'Lexend', fontSize: '0.86rem', color: 'var(--t1)',
    lineHeight: 1.45,
  };
}

// === Helpers for the progressive blocks ===
function blockInputStyle(single) {
  return {
    width: '100%', resize: single ? 'none' : 'vertical', padding: '11px 12px',
    background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 10,
    fontFamily: 'Lexend', fontSize: '0.86rem', lineHeight: 1.55, color: 'var(--t1)',
  };
}
function inspLinkStyle(kind) {
  const isDrive = kind === 'drive';
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 9px', borderRadius: 7,
    background: isDrive ? 'rgba(167,139,250,0.10)' : 'rgba(46,163,242,0.10)',
    border: isDrive ? '1px solid rgba(167,139,250,0.3)' : '1px solid rgba(46,163,242,0.3)',
    color: isDrive ? '#C4B5FD' : 'var(--pl)',
    fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
    textDecoration: 'none',
  };
}
function formatColor(f) {
  return ({ podcast: '#5BBEFC', facecam: '#A78BFA', technique: '#34D399', special: '#F59E0B' }[f] || '#8A9DB8');
}
function formatLabel(f) {
  return ({ podcast: 'Podcast', facecam: 'Facecam', technique: 'Technique', special: 'Spécial' }[f] || f);
}

function ProgressBlock({ num, label, active, validated, onValidate, onUnvalidate, optional, testid, readOnlyLabel, children }) {
  const color = validated ? '#34D399' : (active ? '#2EA3F2' : '#3A4A66');
  return (
    <div
      data-testid={testid}
      style={{
        position: 'relative',
        padding: '14px 16px',
        marginBottom: 14,
        borderRadius: 14,
        background: validated ? 'rgba(52,211,153,0.06)' : (active ? 'var(--sf2)' : 'rgba(58,74,102,0.10)'),
        border: `1.5px solid ${validated ? 'rgba(52,211,153,0.35)' : (active ? 'var(--bd)' : 'rgba(58,74,102,0.4)')}`,
        opacity: active ? 1 : 0.55,
        transition: 'all 0.2s var(--ease-out)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
        <div style={{
          width: 22, height: 22, borderRadius: 7,
          background: `${color}22`, border: `1.5px solid ${color}66`,
          color, display: 'grid', placeItems: 'center',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
          flexShrink: 0,
        }}>{num}</div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5,
          color: active ? 'var(--t1)' : 'var(--t3)',
          textDecoration: active ? 'none' : 'line-through',
          flex: 1,
        }}>
          {label}
          {optional && <span style={{
            marginLeft: 8, fontFamily: 'Sora', fontWeight: 700, fontSize: 9,
            color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>Optionnel</span>}
          {readOnlyLabel && (
            <span style={{
              marginLeft: 8, fontFamily: 'Sora', fontWeight: 600, fontSize: 9,
              color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>{readOnlyLabel}</span>
          )}
        </div>
        {active && !validated && onValidate && (
          <button
            data-testid={`${testid}-validate`}
            onClick={onValidate}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 11px', borderRadius: 999,
              background: 'var(--p)', border: 'none',
              color: '#fff', fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
              cursor: 'pointer',
            }}
          ><Check size={11} /> Valider</button>
        )}
        {validated && onUnvalidate && (
          <button
            data-testid={`${testid}-unvalidate`}
            onClick={onUnvalidate}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '4px 11px', borderRadius: 999,
              background: 'rgba(52,211,153,0.18)', border: '1.5px solid rgba(52,211,153,0.45)',
              color: '#34D399', fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
              cursor: 'pointer',
            }}
          ><Check size={11} /> Validé</button>
        )}
        {validated && !onUnvalidate && (
          <span
            data-testid={`${testid}-validated-readonly`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 10px', borderRadius: 999,
              background: 'rgba(52,211,153,0.14)', border: '1.5px solid rgba(52,211,153,0.35)',
              color: '#34D399', fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
            }}><Check size={10} /> Validé</span>
        )}
        {!active && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 999,
            background: 'rgba(58,74,102,0.18)', border: '1.5px solid rgba(58,74,102,0.4)',
            color: 'var(--t3)', fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
          }}>Verrouillé</span>
        )}
      </div>
      <div style={{ pointerEvents: active ? 'auto' : 'none' }}>{children}</div>
    </div>
  );
}

function BlockField({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
        color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.06em',
        marginBottom: 5, paddingLeft: 2,
      }}>{label}</div>
      {children}
    </div>
  );
}

// === Concept picker (in-task) ===
function ConceptPicker({ concepts, current, onClose, onPick, onCreated, allowCreate = true, projectId }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', format: 'podcast', inspiration_link: '', description: '' });

  const create = async () => {
    if (!form.title.trim()) { toast.error('Titre requis'); return; }
    try {
      const r = await api.post('/concepts', {
        ...form, title: form.title.trim(),
        project_id: projectId || null,
      });
      onCreated(r.data);
      onPick(r.data.id);
    } catch { toast.error('Erreur'); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(6,11,24,0.62)',
        backdropFilter: 'blur(8px)', zIndex: 110,
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="concept-picker-dialog"
        style={{
          width: '100%', maxWidth: 480,
          maxHeight: '85vh', overflowY: 'auto',
          background: 'var(--sf)', border: '1.5px solid var(--bd)',
          borderRadius: 18, padding: 22,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <h3 className="t-display-sm" style={{ margin: 0, flex: 1 }}>
            {creating ? 'Nouveau concept' : 'Choisir un concept'}
          </h3>
          <button onClick={onClose}><X size={16} color="var(--t3)" /></button>
        </div>

        {creating ? (
          <>
            <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Titre *</div>
            <div className="asa-field" style={{ marginBottom: 12 }}>
              <input
                data-testid="cp-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Top 3 conseils business"
                style={{ flex: 1, fontSize: 13.5 }}
              />
            </div>
            <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Format *</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginBottom: 12 }}>
              {[
                { id: 'podcast',   label: 'Podcast' },
                { id: 'facecam',   label: 'Facecam' },
                { id: 'technique', label: 'Technique' },
                { id: 'special',   label: 'Spécial' },
              ].map((f) => (
                <button
                  key={f.id}
                  data-testid={`cp-format-${f.id}`}
                  onClick={() => setForm({ ...form, format: f.id })}
                  style={{
                    padding: '9px 10px', borderRadius: 9,
                    background: form.format === f.id ? `${formatColor(f.id)}1F` : 'var(--sf2)',
                    border: form.format === f.id ? `1.5px solid ${formatColor(f.id)}66` : '1.5px solid var(--bd)',
                    color: form.format === f.id ? formatColor(f.id) : 'var(--t2)',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                    textAlign: 'left', cursor: 'pointer',
                  }}
                >{f.label}</button>
              ))}
            </div>
            <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Lien d&apos;inspiration</div>
            <div className="asa-field" style={{ marginBottom: 12 }}>
              <input
                value={form.inspiration_link}
                onChange={(e) => setForm({ ...form, inspiration_link: e.target.value })}
                placeholder="https://…"
                style={{ flex: 1, fontSize: 12.5 }}
              />
            </div>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Description courte"
              rows={2}
              style={{
                width: '100%', padding: '8px 10px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                borderRadius: 9, color: 'var(--t1)', fontFamily: 'Lexend', fontSize: 12,
                marginBottom: 14,
              }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setCreating(false)} style={{
                flex: 1, padding: '9px 12px', borderRadius: 9,
                background: 'transparent', border: '1.5px solid var(--bd)',
                color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
              }}>Retour</button>
              <button
                onClick={create}
                data-testid="cp-create"
                style={{
                  flex: 1, padding: '9px 12px', borderRadius: 9,
                  background: 'var(--p)', border: 'none',
                  color: '#fff', fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                }}
              >Créer & lier</button>
            </div>
          </>
        ) : (
          <>
            {allowCreate && (
              <button
                onClick={() => setCreating(true)}
                data-testid="cp-new"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 10,
                  background: 'rgba(46,163,242,0.10)',
                  border: '1.5px dashed rgba(46,163,242,0.4)',
                  color: 'var(--pl)', fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                  marginBottom: 14,
                }}
              >+ Créer un nouveau concept</button>
            )}

            {concepts.length === 0 ? (
              <div style={{
                padding: '30px 0', textAlign: 'center',
                fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)',
              }}>Aucun concept pour l&apos;instant.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {concepts.map((c) => {
                  const active = current === c.id;
                  return (
                    <button
                      key={c.id}
                      data-testid={`cp-pick-${c.id}`}
                      onClick={() => onPick(c.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 9,
                        padding: '9px 11px', borderRadius: 9,
                        background: active ? 'rgba(46,163,242,0.12)' : 'var(--sf2)',
                        border: active ? '1.5px solid rgba(46,163,242,0.4)' : '1.5px solid var(--bd)',
                        textAlign: 'left', cursor: 'pointer',
                      }}
                    >
                      <span style={{
                        padding: '2px 7px', borderRadius: 999,
                        background: `${formatColor(c.format)}22`,
                        color: formatColor(c.format),
                        fontFamily: 'Sora', fontWeight: 800, fontSize: 9,
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                        flexShrink: 0,
                      }}>{formatLabel(c.format)} n°{c.format_number}</span>
                      <span style={{ flex: 1, fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: 'var(--t1)' }}>{c.title}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="asa-eyebrow" style={{ marginBottom: 5 }}>{label}</div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create dialog
// ---------------------------------------------------------------------------
function TaskDialog({ users, onClose, onSaved, defaultAssignee = null }) {
  const { user: currentUser } = useAuth();
  const isClient = currentUser?.role === 'client';
  const restrictToRoles = isClient ? ['founder'] : null;
  const [form, setForm] = useState({
    title: '', description: '', stage: 'todo', priority: 'medium',
    assigned_to: defaultAssignee || null, due_date: localToday(),
    inspiration_link: '', drive_link: '', swift_link: '',
    category_tag: null, start_time: '', end_time: '',
    frameio_enabled: false,
    requires_validation: false,
    estimated_minutes: '',
  });
  const [busy, setBusy] = useState(false);

  // Iter47x — When picking a category tag, auto-fill start/end times from the "trame de journée"
  const setCategoryTag = (tagId) => {
    const t = CATEGORY_TAGS[tagId];
    if (!t) { setForm((f) => ({ ...f, category_tag: null })); return; }
    setForm((f) => ({
      ...f,
      category_tag: tagId,
      start_time: f.start_time || t.defaultStart,
      end_time: f.end_time || t.defaultEnd,
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) { toast.error('Titre requis'); return; }
    setBusy(true);
    try {
      const payload = { ...form, due_date: form.due_date || null };
      // Iter43 — clean empty link strings so backend doesn't store noise
      if (!payload.inspiration_link) payload.inspiration_link = null;
      if (!payload.drive_link) delete payload.drive_link;
      if (!payload.swift_link) delete payload.swift_link;
      // Iter61ab — parse estimated_minutes: blank or 0 → null (no alert), else int
      const em = Number.parseInt(payload.estimated_minutes, 10);
      payload.estimated_minutes = Number.isFinite(em) && em > 0 ? em : null;
      await api.post('/tasks', payload);
      toast.success('Tâche créée');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,26,51,0.70)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center',
        padding: 'clamp(8px, 3vw, 18px)',
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 480,
          // Iter61s — leave room for bottom nav (66px) on mobile so nothing gets hidden
          maxHeight: 'min(92vh, calc(100vh - env(safe-area-inset-bottom, 0px) - 80px))',
          overflowY: 'auto',
          background: 'var(--sf)', borderRadius: 18,
          border: '1.5px solid var(--bd)',
          padding: 'clamp(14px, 4vw, 20px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)', flex: 1 }}>
            Nouvelle tâche
          </h2>
          <button type="button" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>

        <Section label="Titre">
          <input
            data-testid="task-title-input"
            autoFocus
            placeholder="Nom de la tâche…"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            style={{
              width: '100%', padding: '12px 14px',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: '0.95rem', color: 'var(--t1)',
            }}
          />
        </Section>

        <Section label="Description">
          <textarea
            placeholder="Détails…"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            style={{
              width: '100%', padding: '12px 14px', resize: 'vertical',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)', lineHeight: 1.5,
            }}
          />
        </Section>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
          <Section label="Étape">
            <select
              data-testid="stage-select"
              value={form.stage}
              onChange={(e) => setForm({ ...form, stage: e.target.value })}
              style={{
                width: '100%', padding: '11px 12px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
                fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)',
              }}
            >
              {STAGE_ORDER.map((s) => <option key={s} value={s}>{STAGES[s].label}</option>)}
            </select>
          </Section>
          <Section label="Priorité">
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              style={{
                width: '100%', padding: '11px 12px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
                fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)',
              }}
            >
              {Object.entries(PRIORITIES).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
            </select>
          </Section>
        </div>

        <Section label="Assigné à">
          <AssigneeRolePicker
            value={form.assigned_to}
            onChange={(v) => setForm({ ...form, assigned_to: v || null })}
            users={users}
            testidPrefix="assignee"
            restrictToRoles={restrictToRoles}
          />
        </Section>

        <Section label="Échéance">
          <input
            type="date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            style={{
              width: '100%', padding: '11px 14px',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)', colorScheme: 'dark',
            }}
          />
        </Section>

        {/* Iter61r bis — Frame.io toggle at task creation.
            Iter61z — toggling Frame.io ON auto-enables requires_validation (Frame.io ALWAYS needs founder review). */}
        <div
          onClick={() => setForm({
            ...form,
            frameio_enabled: !form.frameio_enabled,
            requires_validation: !form.frameio_enabled ? true : form.requires_validation,
          })}
          data-testid="create-frameio-toggle"
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '11px 14px', borderRadius: 12,
            background: form.frameio_enabled ? 'rgba(46,163,242,0.14)' : 'var(--sf2)',
            border: `1.5px solid ${form.frameio_enabled ? 'rgba(46,163,242,0.40)' : 'var(--bd)'}`,
            cursor: 'pointer',
          }}
        >
          <div style={{
            width: 22, height: 22, borderRadius: 6,
            background: form.frameio_enabled ? '#5BBEFC' : 'transparent',
            border: `2px solid ${form.frameio_enabled ? '#5BBEFC' : 'var(--bd)'}`,
            display: 'grid', placeItems: 'center', flexShrink: 0,
            color: '#fff', fontSize: 13, fontWeight: 800,
          }}>{form.frameio_enabled ? '✓' : ''}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)' }}>
              🎬 Ajouter la fonctionnalité Frame.io ?
            </div>
            <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>
              Le monteur pourra soumettre un lien Frame.io, tu recevras une notif et valideras en 1 tap.
            </div>
          </div>
        </div>

        {/* Iter61z — Validation founder required ? Disabled when Frame.io is on (auto-locked to true). */}
        <div
          onClick={() => {
            if (form.frameio_enabled) return; // locked ON when Frame.io is active
            setForm({ ...form, requires_validation: !form.requires_validation });
          }}
          data-testid="create-requires-validation-toggle"
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '11px 14px', borderRadius: 12,
            background: form.requires_validation ? 'rgba(245,166,35,0.14)' : 'var(--sf2)',
            border: `1.5px solid ${form.requires_validation ? 'rgba(245,166,35,0.40)' : 'var(--bd)'}`,
            cursor: form.frameio_enabled ? 'not-allowed' : 'pointer',
            opacity: form.frameio_enabled ? 0.85 : 1,
          }}
        >
          <div style={{
            width: 22, height: 22, borderRadius: 6,
            background: form.requires_validation ? '#F5A623' : 'transparent',
            border: `2px solid ${form.requires_validation ? '#F5A623' : 'var(--bd)'}`,
            display: 'grid', placeItems: 'center', flexShrink: 0,
            color: '#fff', fontSize: 13, fontWeight: 800,
          }}>{form.requires_validation ? '✓' : ''}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)' }}>
              ✅ Nécessite ma validation avant clôture ?
              {form.frameio_enabled && (
                <span style={{ marginLeft: 6, fontSize: 10, color: '#F5A623', fontWeight: 600 }}>
                  (auto — Frame.io)
                </span>
              )}
            </div>
            <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>
              {form.requires_validation
                ? 'Le monteur envoie « J\'ai terminé » → tu valides pour clôturer.'
                : 'Le monteur peut clôturer la tâche lui-même, aucune validation demandée.'}
            </div>
          </div>
        </div>

        {/* Iter61ab — Founder-only time estimate. Hidden from contributor / client.
            If set + chrono exceeds it, we get an alert. Left blank = no alert. */}
        {!isClient && (
          <div
            data-testid="create-estimated-minutes-wrapper"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '11px 14px', borderRadius: 12,
              background: 'var(--sf2)', border: '1.5px solid var(--bd)',
            }}
          >
            <div style={{
              width: 22, height: 22, borderRadius: 6,
              background: form.estimated_minutes ? 'rgba(245,166,35,0.30)' : 'transparent',
              display: 'grid', placeItems: 'center', flexShrink: 0, fontSize: 13,
            }}>⏱</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)' }}>
                Temps estimé (min) — <span style={{ color: 'var(--t3)', fontWeight: 500, fontSize: 11 }}>invisible pour le monteur</span>
              </div>
              <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginTop: 4 }}>
                Alerte auto si le monteur dépasse cette durée. Vide = pas d&apos;alerte.
              </div>
              <input
                data-testid="create-estimated-minutes-input"
                type="number"
                min={0}
                step={5}
                value={form.estimated_minutes}
                onChange={(e) => setForm({ ...form, estimated_minutes: e.target.value })}
                placeholder="Ex: 45"
                style={{
                  marginTop: 6, width: 110, padding: '7px 10px',
                  background: 'var(--bg)', border: '1.5px solid var(--bd)', borderRadius: 8,
                  fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)', outline: 'none',
                  fontVariantNumeric: 'tabular-nums',
                }}
              />
            </div>
          </div>
        )}

        {/* Iter60d — Tags conditional on assignee role:
             - Monteur (contributor)  → 4 category tags + auto time slot
             - Founder                → 2 tags (Expansion / Delivery), NO auto time
             - Client                 → NO tags, NO auto time
             - No assignee yet        → hide the section */}
        {(() => {
          const assignedUser = users.find((u) => u.id === form.assigned_to);
          const roleOfAssignee = assignedUser?.role;
          if (!roleOfAssignee || roleOfAssignee === 'client') return null;
          const tagKeys = roleOfAssignee === 'founder' ? FOUNDER_TAG_ORDER : CATEGORY_TAG_ORDER;
          const showTimeSlot = roleOfAssignee === 'contributor';
          return (
            <>
              <Section label="Catégorie">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                  {tagKeys.map((k) => {
                    const t = CATEGORY_TAGS[k];
                    const active = form.category_tag === k;
                    return (
                      <button
                        key={k}
                        type="button"
                        data-testid={`tag-select-${k}`}
                        onClick={() => setCategoryTag(active ? null : k)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 7,
                          padding: '9px 12px', borderRadius: 10,
                          background: active ? t.bg : 'var(--sf2)',
                          border: `1.5px solid ${active ? t.border : 'var(--bd)'}`,
                          color: active ? t.color : 'var(--t2)',
                          fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                          cursor: 'pointer', textAlign: 'left',
                        }}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </Section>

              {showTimeSlot && (
                <Section label="Créneau horaire">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input
              type="time"
              data-testid="task-start-time"
              value={form.start_time || ''}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              placeholder="Début"
              style={{
                width: '100%', padding: '11px 12px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
                fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)', colorScheme: 'dark',
              }}
            />
            <input
              type="time"
              data-testid="task-end-time"
              value={form.end_time || ''}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              placeholder="Fin"
              style={{
                width: '100%', padding: '11px 12px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
                fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)', colorScheme: 'dark',
              }}
            />
          </div>
                </Section>
              )}
            </>
          );
        })()}

        {/* Iter43 — Liens optionnels (inspiration / Drive / SwissTransfer) */}
        <Section label="Lien d'inspiration (optionnel)">
          <input
            data-testid="task-inspiration-input"
            type="url"
            placeholder="https://..."
            value={form.inspiration_link}
            onChange={(e) => setForm({ ...form, inspiration_link: e.target.value })}
            style={{
              width: '100%', padding: '11px 12px',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)',
            }}
          />
        </Section>

        <Section label="Lien Drive (optionnel)">
          <input
            data-testid="task-drive-input"
            type="url"
            placeholder="https://drive.google.com/..."
            value={form.drive_link}
            onChange={(e) => setForm({ ...form, drive_link: e.target.value })}
            style={{
              width: '100%', padding: '11px 12px',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)',
            }}
          />
        </Section>

        <Section label="Lien SwissTransfer (optionnel)">
          <input
            data-testid="task-swift-input"
            type="url"
            placeholder="https://swisstransfer.com/..."
            value={form.swift_link}
            onChange={(e) => setForm({ ...form, swift_link: e.target.value })}
            style={{
              width: '100%', padding: '11px 12px',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)',
            }}
          />
        </Section>

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>
            Annuler
          </button>
          <button
            data-testid="task-save-btn"
            type="submit"
            disabled={busy || !form.title.trim()}
            className="asa-btn"
            style={{ flex: 1 }}
          >
            {busy ? <Loader2 size={15} className="spin" /> : 'Créer'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ===========================================================================
// Iter47h — Grouped tasks (poupée russe)
// ===========================================================================

const GROUP_COLORS = [
  { id: 'pink',    hex: '#EC4899', label: 'Rose' },
  { id: 'purple',  hex: '#A78BFA', label: 'Violet' },
  { id: 'blue',    hex: '#5BBEFC', label: 'Bleu' },
  { id: 'teal',    hex: '#2DD4BF', label: 'Turquoise' },
  { id: 'green',   hex: '#34D399', label: 'Vert' },
  { id: 'amber',   hex: '#F5A623', label: 'Orange' },
  { id: 'red',     hex: '#EF4444', label: 'Rouge' },
  { id: 'slate',   hex: '#94A3B8', label: 'Gris' },
];

// Wrapper that first asks: single task or grouped task.
function CreateTaskFlow({ users, onClose, onSaved, defaultAssignee = null }) {
  const [mode, setMode] = useState('chooser'); // 'chooser' | 'single' | 'group'

  if (mode === 'single') {
    return <TaskDialog users={users} defaultAssignee={defaultAssignee} onClose={onClose} onSaved={onSaved} />;
  }
  if (mode === 'group') {
    return <TaskGroupDialog users={users} defaultAssignee={defaultAssignee} onClose={onClose} onSaved={onSaved} onBack={() => setMode('chooser')} />;
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,26,51,0.70)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 520,
          background: 'var(--sf)', borderRadius: 22,
          border: '1.5px solid var(--bd)', padding: 26,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)', flex: 1 }}>
            Nouvelle tâche
          </h2>
          <button type="button" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>
        <p style={{ margin: '0 0 20px', fontFamily: 'Lexend', fontSize: 13, color: 'var(--t3)' }}>
          Choisis le format qui correspond à ta prod.
        </p>

        <div style={{ display: 'grid', gap: 12 }}>
          <button
            type="button"
            data-testid="create-mode-single"
            onClick={() => setMode('single')}
            style={{
              display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
              padding: 18, borderRadius: 16, cursor: 'pointer',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)',
              transition: 'all 0.2s var(--ease-out)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#5BBEFC'; e.currentTarget.style.background = 'rgba(91,190,252,0.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.background = 'var(--sf2)'; }}
          >
            <div style={{
              width: 46, height: 46, borderRadius: 12,
              background: 'linear-gradient(135deg, #5BBEFC, #2EA3F2)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <Check size={20} color="#fff" strokeWidth={2.5} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: 'var(--t1)', marginBottom: 2 }}>
                Tâche unique
              </div>
              <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', lineHeight: 1.4 }}>
                Une action ponctuelle avec titre, deadline et assigné.
              </div>
            </div>
            <ChevronRight size={18} color="var(--t3)" />
          </button>

          <button
            type="button"
            data-testid="create-mode-group"
            onClick={() => setMode('group')}
            style={{
              display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
              padding: 18, borderRadius: 16, cursor: 'pointer',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)',
              transition: 'all 0.2s var(--ease-out)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#EC4899'; e.currentTarget.style.background = 'rgba(236,72,153,0.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.background = 'var(--sf2)'; }}
          >
            <div style={{
              width: 46, height: 46, borderRadius: 12,
              background: 'linear-gradient(135deg, #EC4899, #A78BFA)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <LayoutGrid size={20} color="#fff" strokeWidth={2.4} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: 'var(--t1)', marginBottom: 2 }}>
                Tâche groupée <span style={{ fontSize: 10, background: '#EC489922', color: '#EC4899', padding: '2px 7px', borderRadius: 999, marginLeft: 6, letterSpacing: '0.05em' }}>NOUVEAU</span>
              </div>
              <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', lineHeight: 1.4 }}>
                Un événement client déroulé en plusieurs étapes chronologiques.
              </div>
            </div>
            <ChevronRight size={18} color="var(--t3)" />
          </button>
        </div>
      </div>
    </div>
  );
}


// Form dialog for creating a grouped task (parent + chronological steps)
function TaskGroupDialog({ users, onClose, onSaved, onBack, defaultAssignee = null }) {
  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [color, setColor] = useState(GROUP_COLORS[0].hex);
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState(defaultAssignee || '');
  const _todayGrp = localToday();
  const [steps, setSteps] = useState([
    { title: '', due_date: _todayGrp, assigned_to: defaultAssignee || '' },
    { title: '', due_date: _todayGrp, assigned_to: defaultAssignee || '' },
  ]);
  const [busy, setBusy] = useState(false);

  const updateStep = (idx, patch) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const addStep = () => setSteps((prev) => [...prev, { title: '', due_date: _todayGrp, assigned_to: defaultAssignee || '' }]);
  const removeStep = (idx) => setSteps((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error('Nom du groupe requis'); return; }
    const validSteps = steps.filter((s) => s.title.trim());
    if (validSteps.length === 0) { toast.error('Au moins une étape est requise'); return; }
    setBusy(true);
    try {
      await api.post('/tasks/groups', {
        name: name.trim(),
        color,
        description,
        assigned_to: assignedTo || null,
        client: client.trim() || null,
        steps: validSteps.map((s, i) => ({
          title: s.title.trim() || `Étape ${i + 1}`,
          due_date: s.due_date || null,
          assigned_to: s.assigned_to || null,
        })),
      });
      toast.success(`Tâche groupée créée · ${validSteps.length} étape${validSteps.length > 1 ? 's' : ''}`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,26,51,0.70)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 18,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 620, maxHeight: '92vh', overflowY: 'auto',
          background: 'var(--sf)', borderRadius: 22,
          border: '1.5px solid var(--bd)', padding: 26,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4, gap: 8 }}>
          {onBack && (
            <button type="button" onClick={onBack} title="Retour">
              <ArrowLeft size={16} color="var(--t3)" />
            </button>
          )}
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)', flex: 1 }}>
            Tâche groupée
          </h2>
          <button type="button" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>
        <p style={{ margin: '0 0 18px', fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)' }}>
          Un événement (ex : « Nash · dates importantes ») décomposé en étapes chronologiques.
        </p>

        <Section label="Nom du groupe">
          <input
            data-testid="group-name-input"
            autoFocus
            placeholder="Ex : Nash · dates importantes"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              width: '100%', padding: '12px 14px',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: '0.95rem', color: 'var(--t1)',
            }}
          />
        </Section>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
          <Section label="Client (optionnel)">
            <input
              data-testid="group-client-input"
              placeholder="Ex : Nash"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              style={{
                width: '100%', padding: '11px 12px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
                fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)',
              }}
            />
          </Section>
          <Section label="Assigné par défaut">
            <select
              data-testid="group-assignee-select"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              style={{
                width: '100%', padding: '11px 12px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
                fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)',
              }}
            >
              <option value="">Non assigné</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Section>
        </div>

        <Section label="Couleur client">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {GROUP_COLORS.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`group-color-${c.id}`}
                onClick={() => setColor(c.hex)}
                title={c.label}
                style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: c.hex,
                  border: color === c.hex ? '2.5px solid #fff' : '2.5px solid transparent',
                  boxShadow: color === c.hex ? `0 0 0 2px ${c.hex}` : 'none',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              />
            ))}
          </div>
        </Section>

        <Section label="Description (optionnel)">
          <textarea
            placeholder="Contexte, objectifs, notes internes…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            style={{
              width: '100%', padding: '11px 14px', resize: 'vertical',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: '0.85rem', color: 'var(--t1)', lineHeight: 1.5,
            }}
          />
        </Section>

        <div className="asa-eyebrow" style={{ marginBottom: 10 }}>
          Étapes chronologiques ({steps.length})
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {steps.map((s, i) => (
            <div
              key={i}
              data-testid={`group-step-row-${i}`}
              style={{
                display: 'grid', gridTemplateColumns: '30px 1fr 130px 130px 32px',
                gap: 8, alignItems: 'center',
                padding: '10px 12px', background: 'var(--sf2)', borderRadius: 12,
                border: '1.5px solid var(--bd)',
                borderLeft: `4px solid ${color}`,
              }}
            >
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                background: color, color: '#fff',
                display: 'grid', placeItems: 'center',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
              }}>
                {i + 1}
              </div>
              <input
                data-testid={`group-step-title-${i}`}
                placeholder={`Titre étape ${i + 1}`}
                value={s.title}
                onChange={(e) => updateStep(i, { title: e.target.value })}
                style={{
                  padding: '8px 10px', background: 'var(--sf)',
                  border: '1px solid var(--bd)', borderRadius: 8,
                  fontFamily: 'Lexend', fontSize: '0.85rem', color: 'var(--t1)',
                }}
              />
              <input
                type="date"
                value={s.due_date}
                onChange={(e) => updateStep(i, { due_date: e.target.value })}
                style={{
                  padding: '8px 10px', background: 'var(--sf)',
                  border: '1px solid var(--bd)', borderRadius: 8,
                  fontFamily: 'Lexend', fontSize: '0.78rem', color: 'var(--t1)', colorScheme: 'dark',
                }}
              />
              <select
                value={s.assigned_to}
                onChange={(e) => updateStep(i, { assigned_to: e.target.value })}
                title="Assigné à cette étape"
                style={{
                  padding: '8px 6px', background: 'var(--sf)',
                  border: '1px solid var(--bd)', borderRadius: 8,
                  fontFamily: 'Lexend', fontSize: '0.78rem', color: 'var(--t1)',
                }}
              >
                <option value="">Par défaut</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <button
                type="button"
                onClick={() => removeStep(i)}
                disabled={steps.length <= 1}
                style={{
                  background: 'transparent', border: 'none',
                  cursor: steps.length > 1 ? 'pointer' : 'not-allowed',
                  opacity: steps.length > 1 ? 1 : 0.3,
                }}
                title="Retirer cette étape"
              >
                <Trash2 size={14} color="var(--t3)" />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          data-testid="group-add-step-btn"
          onClick={addStep}
          className="asa-btn-ghost asa-btn"
          style={{ width: '100%', marginBottom: 20, borderStyle: 'dashed' }}
        >
          <Plus size={14} /> Ajouter une étape
        </button>

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>
            Annuler
          </button>
          <button
            data-testid="group-save-btn"
            type="submit"
            disabled={busy || !name.trim()}
            className="asa-btn"
            style={{ flex: 1, background: `linear-gradient(135deg, ${color}, ${color}CC)`, border: 'none' }}
          >
            {busy ? <Loader2 size={15} className="spin" /> : `Créer le groupe (${steps.filter((s) => s.title.trim()).length})`}
          </button>
        </div>
      </form>
    </div>
  );
}


// GroupCard — renders a group parent + its steps as a russian-doll card.
// Steps timeline: horizontal progress bar + vertical stepper below.
function GroupCard({ group, steps, users, onStepClick, onEditStep, onAddStep, onDeleteGroup, onRenameGroup, canManage }) {
  const [expanded, setExpanded] = useState(true);
  const [addingStep, setAddingStep] = useState(false);
  const [newStepTitle, setNewStepTitle] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(group.group_name || group.title || '');
  const color = group.group_color || '#5BBEFC';
  const sortedSteps = [...steps].sort((a, b) => (a.step_order ?? 999) - (b.step_order ?? 999));
  const total = sortedSteps.length;
  const done = sortedSteps.filter((s) => s.stage === 'done').length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const anyLate = sortedSteps.some((s) => {
    if (s.stage === 'done' || !s.due_date) return false;
    return new Date(s.due_date) < new Date();
  });
  // Group-level "phase" = phase of least advanced step
  const phaseOrder = { todo: 0, in_progress: 1, review: 2, done: 3 };
  const minPhase = sortedSteps.reduce((acc, s) => {
    const p = phaseOrder[s.stage] ?? 0;
    return p < acc ? p : acc;
  }, 3);
  const groupPhaseLabel = total === 0 ? 'Vide'
    : done === total ? 'Terminé'
    : minPhase === 0 ? 'À faire'
    : minPhase === 1 ? 'En cours'
    : 'À valider';

  const handleAdd = async () => {
    if (!newStepTitle.trim()) { toast.error('Titre requis'); return; }
    await onAddStep(group.id, newStepTitle.trim());
    setNewStepTitle('');
    setAddingStep(false);
  };

  return (
    <div
      data-testid={`group-card-${group.id}`}
      style={{
        borderRadius: 18,
        border: `1.5px solid ${color}55`,
        background: `linear-gradient(180deg, ${color}0F, var(--sf))`,
        overflow: 'hidden',
        transition: 'all 0.2s var(--ease-out)',
      }}
    >
      <div
        style={{
          padding: '16px 18px',
          display: 'flex', alignItems: 'center', gap: 14,
          cursor: 'pointer',
          borderLeft: `4px solid ${color}`,
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: color, color: '#fff',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <LayoutGrid size={20} strokeWidth={2.4} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            {renaming ? (
              <input
                data-testid={`group-rename-input-${group.id}`}
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const v = renameValue.trim();
                    if (v && v !== (group.group_name || group.title)) {
                      await onRenameGroup?.(group.id, v);
                    }
                    setRenaming(false);
                  }
                  if (e.key === 'Escape') {
                    setRenameValue(group.group_name || group.title || '');
                    setRenaming(false);
                  }
                }}
                onBlur={async () => {
                  const v = renameValue.trim();
                  if (v && v !== (group.group_name || group.title)) {
                    await onRenameGroup?.(group.id, v);
                  }
                  setRenaming(false);
                }}
                style={{
                  fontFamily: 'Sora', fontWeight: 800, fontSize: '1.02rem', color: 'var(--t1)',
                  letterSpacing: '-0.01em',
                  padding: '4px 10px', borderRadius: 8,
                  background: 'var(--sf2)', border: `1.5px solid ${color}88`,
                  minWidth: 220,
                }}
              />
            ) : (
              <span
                data-testid={`group-name-${group.id}`}
                onClick={(e) => { if (canManage && onRenameGroup) { e.stopPropagation(); setRenaming(true); } }}
                title={canManage ? 'Cliquer pour renommer' : ''}
                style={{
                  fontFamily: 'Sora', fontWeight: 800, fontSize: '1.02rem', color: 'var(--t1)',
                  letterSpacing: '-0.01em',
                  cursor: canManage && onRenameGroup ? 'text' : 'default',
                  padding: canManage && onRenameGroup ? '2px 6px' : '0',
                  borderRadius: 6,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { if (canManage && onRenameGroup) e.currentTarget.style.background = 'var(--sf2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >{group.group_name || group.title}</span>
            )}
            {group.client_name && (
              <span style={{
                fontFamily: 'Sora', fontWeight: 700, fontSize: '0.6rem',
                padding: '2px 8px', borderRadius: 999,
                background: `${color}22`, color, textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>{group.client_name}</span>
            )}
            <span style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: '0.58rem',
              padding: '2px 8px', borderRadius: 999,
              background: 'var(--sf2)', color: 'var(--t3)',
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>{groupPhaseLabel}</span>
            {anyLate && (
              <span style={{
                fontFamily: 'Sora', fontWeight: 800, fontSize: '0.58rem',
                padding: '2px 8px', borderRadius: 999,
                background: 'rgba(239,68,68,0.14)', color: '#EF4444',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>En retard</span>
            )}
          </div>
          {/* Baromètre */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, maxWidth: 320, height: 7, borderRadius: 4, background: 'var(--sf2)', overflow: 'hidden' }}>
              <div style={{
                width: `${pct}%`, height: '100%',
                background: `linear-gradient(90deg, ${color}, ${color}AA)`,
                transition: 'width 0.4s var(--ease-out)',
              }} />
            </div>
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.76rem', color }}>
              {done}/{total} · {pct}%
            </span>
          </div>
        </div>
        <ChevronDown
          size={18}
          color="var(--t3)"
          style={{
            flexShrink: 0,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s var(--ease-out)',
          }}
        />
      </div>

      {expanded && (
        <div
          data-testid={`group-steps-${group.id}`}
          style={{ padding: '4px 18px 16px 18px' }}
        >
          {sortedSteps.length === 0 ? (
            <div style={{ padding: '10px 0', fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', fontStyle: 'italic' }}>
              Aucune étape pour le moment.
            </div>
          ) : (
            <div style={{ position: 'relative', paddingLeft: 20 }}>
              {/* Vertical line */}
              <div style={{
                position: 'absolute', top: 12, bottom: 12, left: 9,
                width: 2, background: `${color}33`,
              }} />
              {sortedSteps.map((s, i) => (
                <GroupStepRow
                  key={s.id}
                  idx={i + 1}
                  step={s}
                  color={color}
                  users={users}
                  onClick={() => onStepClick(s)}
                  onEdit={onEditStep}
                />
              ))}
            </div>
          )}

          {canManage && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              {addingStep ? (
                <>
                  <input
                    autoFocus
                    data-testid={`group-quick-add-input-${group.id}`}
                    placeholder={`Titre étape ${sortedSteps.length + 1}`}
                    value={newStepTitle}
                    onChange={(e) => setNewStepTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } if (e.key === 'Escape') { setAddingStep(false); setNewStepTitle(''); } }}
                    style={{
                      flex: 1, padding: '8px 12px',
                      background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 10,
                      fontFamily: 'Lexend', fontSize: '0.85rem', color: 'var(--t1)',
                    }}
                  />
                  <button
                    type="button"
                    data-testid={`group-quick-add-confirm-${group.id}`}
                    onClick={handleAdd}
                    className="asa-btn"
                    style={{ padding: '0 14px', background: color, border: 'none' }}
                  >Ajouter</button>
                  <button type="button" onClick={() => { setAddingStep(false); setNewStepTitle(''); }}>
                    <X size={16} color="var(--t3)" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    data-testid={`group-add-step-inline-${group.id}`}
                    onClick={() => setAddingStep(true)}
                    className="asa-btn-ghost asa-btn"
                    style={{ borderStyle: 'dashed', flex: 1 }}
                  >
                    <Plus size={14} /> Ajouter une étape
                  </button>
                  {onDeleteGroup && (
                    <button
                      type="button"
                      data-testid={`group-delete-${group.id}`}
                      onClick={() => {
                        if (window.confirm(`Supprimer le groupe « ${group.group_name || group.title} » et toutes ses étapes ?`)) {
                          onDeleteGroup(group.id);
                        }
                      }}
                      className="asa-btn-ghost asa-btn"
                      style={{ padding: '0 12px', color: '#EF4444' }}
                      title="Supprimer le groupe"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function GroupStepRow({ idx, step, color, users, onClick, onEdit }) {
  const assignee = users.find((u) => u.id === step.assigned_to);
  const done = step.stage === 'done';
  const inProgress = step.stage === 'in_progress' || step.stage === 'review';
  const overdue = step.due_date && new Date(step.due_date) < new Date() && !done;
  const dueLabel = step.due_date ? new Date(step.due_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : null;

  return (
    <div
      data-testid={`group-step-${step.id}`}
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 12px', marginBottom: 8, marginLeft: 4,
        borderRadius: 12,
        background: 'var(--sf)', border: '1.5px solid var(--bd)',
        cursor: 'pointer',
        transition: 'all 0.15s var(--ease-out)',
        opacity: done ? 0.7 : 1,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${color}77`; e.currentTarget.style.transform = 'translateX(2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.transform = 'translateX(0)'; }}
    >
      {/* Step dot on the vertical line */}
      <div style={{
        position: 'absolute', left: -19, top: '50%', transform: 'translateY(-50%)',
        width: 20, height: 20, borderRadius: '50%',
        background: done ? color : inProgress ? `${color}55` : 'var(--sf2)',
        border: `2.5px solid ${color}`,
        display: 'grid', placeItems: 'center',
        boxShadow: done ? `0 0 0 3px ${color}22` : 'none',
      }}>
        {done ? <Check size={11} color="#fff" strokeWidth={3} /> : (
          <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 9, color }}>{idx}</span>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: '0.88rem',
          color: done ? 'var(--t3)' : 'var(--t1)',
          textDecoration: done ? 'line-through' : 'none',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {step.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.55rem',
            padding: '2px 7px', borderRadius: 999,
            background: done ? 'rgba(52,211,153,0.16)' : inProgress ? 'rgba(46,163,242,0.16)' : 'var(--sf2)',
            color: done ? '#34D399' : inProgress ? '#2EA3F2' : 'var(--t3)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            {(STAGES[step.stage] || STAGES.todo).label}
          </span>
          {dueLabel && (
            <span style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: '0.7rem',
              color: overdue ? '#EF4444' : 'var(--t3)',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <Clock size={10} /> {dueLabel}{overdue ? ' · en retard' : ''}
            </span>
          )}
          {assignee && (
            <span style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: '0.68rem',
              color: 'var(--t3)',
            }}>
              · {assignee.name}
            </span>
          )}
        </div>
      </div>

      {onEdit && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(step); }}
          style={{
            background: 'transparent', border: 'none',
            padding: 4, borderRadius: 6, cursor: 'pointer',
            opacity: 0.6,
          }}
          title="Modifier"
        >
          <Pencil size={13} color="var(--t3)" />
        </button>
      )}
    </div>
  );
}

// ===========================================================================
// MonthCalendar — month grid view of tasks (mini colored pills per day)
// ===========================================================================
const DAYS_FR = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'];
const STAGE_COLORS = {
  todo:        '#8A9DB8',
  in_progress: '#2EA3F2',
  review:      '#F5A623',
  done:        '#34D399',
};

function MonthCalendar({ tasks, projects = [], cursor, onPrev, onNext, onSelect }) {
  const projColor = (pid) => {
    const p = projects.find((x) => x.id === pid);
    return p?.color || null;
  };
  const monthLabel = cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Iter47z — LOCAL timezone (Paris) — avoid UTC shift
  const _todayD = new Date();
  const today = `${_todayD.getFullYear()}-${String(_todayD.getMonth() + 1).padStart(2, '0')}-${String(_todayD.getDate()).padStart(2, '0')}`;

  // Build 42 cells (6 weeks max)
  const cells = [];
  for (let i = 0; i < startOffset; i++) {
    const d = new Date(year, month, 1 - (startOffset - i));
    cells.push({ date: d, outside: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), outside: false });
  }
  while (cells.length < 42) {
    const lastDate = cells[cells.length - 1].date;
    const next = new Date(lastDate);
    next.setDate(next.getDate() + 1);
    cells.push({ date: next, outside: true });
  }

  // Index tasks by ISO day
  const byDay = {};
  tasks.forEach((t) => {
    if (!t.due_date) return;
    const k = t.due_date.slice(0, 10);
    (byDay[k] = byDay[k] || []).push(t);
  });

  const totalThisMonth = tasks.filter((t) => t.due_date && t.due_date.slice(0, 7) === `${year}-${String(month + 1).padStart(2, '0')}`).length;

  return (
    <div>
      {/* Month nav */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: 14, padding: '4px 0',
      }}>
        <button onClick={onPrev} data-testid="month-prev" style={{
          width: 30, height: 30, borderRadius: 8,
          background: 'var(--sf)', border: '1.5px solid var(--bd)',
          display: 'grid', placeItems: 'center', cursor: 'pointer',
        }}><ChevronLeft size={13} color="var(--t2)" /></button>
        <span style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: '1.05rem',
          color: 'var(--t1)', textTransform: 'capitalize', letterSpacing: '-0.01em',
        }}>{monthLabel}</span>
        <button onClick={onNext} data-testid="month-next" style={{
          width: 30, height: 30, borderRadius: 8,
          background: 'var(--sf)', border: '1.5px solid var(--bd)',
          display: 'grid', placeItems: 'center', cursor: 'pointer',
        }}><ChevronRight size={13} color="var(--t2)" /></button>

        <div style={{ flex: 1 }} />
        <span style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem',
          color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>{totalThisMonth} tâche{totalThisMonth > 1 ? 's' : ''} ce mois</span>
      </div>

      {/* Day-of-week header */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 1, marginBottom: 6,
      }}>
        {DAYS_FR.map((d) => (
          <div key={d} style={{
            padding: '6px 8px',
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.62rem',
            color: 'var(--t3)', letterSpacing: '0.08em',
          }}>{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 6,
      }}>
        {cells.map((c, i) => {
          // Iter47z — Format in LOCAL timezone (Paris)
          const k = `${c.date.getFullYear()}-${String(c.date.getMonth() + 1).padStart(2, '0')}-${String(c.date.getDate()).padStart(2, '0')}`;
          const items = byDay[k] || [];
          const isToday = k === today;
          return (
            <div
              key={i}
              data-testid={`month-day-${k}`}
              style={{
                minHeight: 110,
                background: c.outside ? 'transparent' : 'var(--sf)',
                border: `1.5px solid ${isToday ? 'var(--p)' : 'var(--bd)'}`,
                borderRadius: 10,
                padding: '6px 7px',
                opacity: c.outside ? 0.4 : 1,
                display: 'flex', flexDirection: 'column', gap: 3,
                overflow: 'hidden',
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 2,
              }}>
                <span style={{
                  fontFamily: 'Sora', fontWeight: 700, fontSize: '0.82rem',
                  color: isToday ? 'var(--pl)' : 'var(--t1)',
                }}>{c.date.getDate()}</span>
                {items.length > 0 && (
                  <span style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: '0.6rem',
                    color: 'var(--t3)',
                  }}>{items.length}</span>
                )}
              </div>
              {items.slice(0, 3).map((t) => {
                const color = projColor(t.project_id) || STAGE_COLORS[t.stage] || '#8A9DB8';
                const isDone = t.stage === 'done';
                return (
                  <button
                    key={t.id}
                    onClick={() => onSelect(t)}
                    data-testid={`month-task-${t.id}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '3px 6px', borderRadius: 5,
                      background: `${color}1A`, border: `1px solid ${color}40`,
                      textAlign: 'left', cursor: 'pointer',
                      opacity: isDone ? 0.55 : 1,
                    }}
                  >
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <span style={{
                      fontFamily: 'Lexend', fontSize: '0.66rem', color: 'var(--t1)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      textDecoration: isDone ? 'line-through' : 'none',
                    }}>{t.title}</span>
                  </button>
                );
              })}
              {items.length > 3 && (
                <span style={{
                  fontFamily: 'Lexend', fontSize: '0.6rem', color: 'var(--t3)',
                  padding: '0 6px',
                }}>+ {items.length - 3} de plus</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// TasksFromDocDialog — paste a brief / planning text → LLM extracts tasks with dates
// ============================================================================
function TasksFromDocDialog({ users, onClose, onSaved }) {
  const [text, setText] = useState('');
  const [assignee, setAssignee] = useState(null);
  const [defaultDue, setDefaultDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('input'); // input | review
  const [drafts, setDrafts] = useState([]);

  const monteurs = users; // Iter37 — allow founders to be assigned too

  const generate = async () => {
    if (!text.trim() || text.trim().length < 20) {
      toast.error('Colle un texte plus long (au moins 20 caractères)');
      return;
    }
    if (assignee && !defaultDue) {
      toast.error('Choisis une deadline pour ' + (users.find((u) => u.id === assignee)?.name || 'le monteur'));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/tasks/from-doc', {
        text: text.trim(),
        default_assignee: assignee || null,
        default_due_date: defaultDue || null,
      });
      const list = r.data?.created || [];
      if (!list.length) {
        toast.error('Aucune tâche détectée');
        return;
      }
      setDrafts(list);
      setStep('review');
      toast.success(`${list.length} tâche${list.length > 1 ? 's' : ''} créée${list.length > 1 ? 's' : ''} — visible dans Planning`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'IA indisponible');
    } finally {
      setBusy(false);
    }
  };

  const finish = () => { onSaved(); };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,26,51,0.70)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto',
          background: 'var(--sf)', borderRadius: 22,
          border: '1.5px solid var(--bd)', padding: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 11,
            background: 'linear-gradient(135deg, #A78BFA 0%, #5BBEFC 100%)',
            display: 'grid', placeItems: 'center', marginRight: 10,
          }}>
            <Sparkles size={17} color="#fff" />
          </div>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)', flex: 1 }}>
            IA depuis un document
          </h2>
          <button type="button" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>
        <p style={{
          fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)',
          marginTop: 0, marginBottom: 18, lineHeight: 1.5,
        }}>
          Colle un brief, planning ou agenda — l&apos;IA va extraire les tâches avec leur date d&apos;échéance et les placer dans le calendrier.
        </p>

        {step === 'input' ? (
          <>
            <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Document / planning</div>
            <textarea
              data-testid="ai-doc-textarea"
              autoFocus
              placeholder={`Ex:\n• Lundi 3 mars 10h : Réunion équipe Nash\n• Mardi 4 mars : Livrer le montage Vidéo 2 Yves\n• Vendredi : Appel client à 14h\n…`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              style={{
                width: '100%', padding: '12px 14px', resize: 'vertical',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
                fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)', lineHeight: 1.55,
              }}
            />

            {monteurs.length > 0 && (
              <>
                <div className="asa-eyebrow" style={{ marginTop: 14, marginBottom: 8 }}>
                  Assigner toutes les tâches à (optionnel)
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setAssignee(null)}
                    style={{
                      padding: '6px 11px', borderRadius: 999,
                      background: assignee === null ? 'var(--p)' : 'var(--sf2)',
                      border: `1.5px solid ${assignee === null ? 'var(--p)' : 'var(--bd)'}`,
                      color: assignee === null ? '#fff' : 'var(--t2)',
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                    }}
                  >Personne</button>
                  {users.map((u) => {
                    const active = assignee === u.id;
                    return (
                      <button
                        key={u.id} type="button"
                        data-testid={`ai-doc-assignee-${u.id}`}
                        onClick={() => setAssignee(active ? null : u.id)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '6px 11px', borderRadius: 999,
                          background: active ? `${u.color}26` : 'var(--sf2)',
                          border: `1.5px solid ${active ? u.color : 'var(--bd)'}`,
                          color: active ? u.color : 'var(--t2)',
                          fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                        }}
                      >
                        <Avatar user={u} size={18} />
                        {u.name}
                      </button>
                    );
                  })}
                </div>
                {assignee && (
                  <div style={{
                    marginTop: 14,
                    padding: 14, borderRadius: 12,
                    background: 'rgba(245,166,35,0.08)',
                    border: '1.5px solid rgba(245,166,35,0.35)',
                  }}>
                    <div className="asa-eyebrow" style={{ marginBottom: 8, color: '#F5A623' }}>
                      Deadline pour {users.find((u) => u.id === assignee)?.name} *
                    </div>
                    <input
                      data-testid="ai-doc-default-due"
                      type="date"
                      value={defaultDue}
                      onChange={(e) => setDefaultDue(e.target.value)}
                      style={{
                        width: '100%', padding: '10px 12px',
                        background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 10,
                        fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)',
                      }}
                    />
                    <div style={{ marginTop: 6, fontFamily: 'Lexend', fontSize: 10.5, color: 'var(--t3)' }}>
                      S&apos;applique aux tâches sans date détectée par l&apos;IA. Toutes les tâches générées seront visibles sur son planning.
                    </div>
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>Annuler</button>
              <button
                type="button"
                data-testid="ai-doc-generate"
                onClick={generate}
                disabled={busy}
                className="asa-btn"
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #A78BFA, #5BBEFC)',
                  border: 'none',
                }}
              >
                {busy ? <><Loader2 size={14} className="spin" /> Génération…</> : <><Sparkles size={14} /> Générer</>}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{
              padding: '10px 12px', borderRadius: 10, marginBottom: 14,
              background: 'rgba(52,211,153,0.10)',
              border: '1.5px solid rgba(52,211,153,0.35)',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: '#34D399',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Check size={14} />
              {drafts.length} tâche{drafts.length > 1 ? 's' : ''} créée{drafts.length > 1 ? 's' : ''} — modifie si besoin puis va dans le calendrier
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflow: 'auto' }}>
              {drafts.map((t) => (
                <AIDraftCard
                  key={t.id}
                  task={t}
                  users={users}
                  onUpdate={(patch) => {
                    // optimistic local update
                    setDrafts((arr) => arr.map((x) => x.id === t.id ? { ...x, ...patch } : x));
                    api.patch(`/tasks/${t.id}`, patch).catch(() => toast.error('Erreur sauvegarde'));
                  }}
                  onDelete={() => {
                    setDrafts((arr) => arr.filter((x) => x.id !== t.id));
                    api.delete(`/tasks/${t.id}`).catch(() => {});
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              data-testid="ai-doc-finish"
              onClick={finish}
              className="asa-btn"
              style={{ width: '100%', marginTop: 16 }}
            >
              Voir dans le calendrier
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Inline editable card for AI-generated task in the review step
function AIDraftCard({ task, users, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [due, setDue] = useState(task.due_date || '');
  const [startT, setStartT] = useState(task.start_time || '');
  const [assignee, setAssignee] = useState(task.assigned_to || '');

  const save = () => {
    const patch = {
      title: title.trim() || task.title,
      due_date: due || null,
      start_time: startT || null,
      assigned_to: assignee || null,
    };
    onUpdate(patch);
    setEditing(false);
  };

  return (
    <div
      data-testid={`ai-doc-task-${task.id}`}
      style={{
        padding: '10px 12px', borderRadius: 9,
        background: 'var(--sf2)', border: '1.5px solid var(--bd)',
      }}
    >
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input
            data-testid={`ai-task-title-input-${task.id}`}
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titre"
            style={{
              padding: '7px 9px', fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
              background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 8, color: 'var(--t1)',
            }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="date"
              data-testid={`ai-task-due-input-${task.id}`}
              value={due}
              onChange={(e) => setDue(e.target.value)}
              style={{
                flex: 1, padding: '7px 9px', fontFamily: 'Lexend', fontSize: 12,
                background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 8, color: 'var(--t1)', colorScheme: 'dark',
              }}
            />
            <input
              type="time"
              value={startT}
              onChange={(e) => setStartT(e.target.value)}
              style={{
                width: 100, padding: '7px 9px', fontFamily: 'Lexend', fontSize: 12,
                background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 8, color: 'var(--t1)', colorScheme: 'dark',
              }}
            />
          </div>
          <select
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            data-testid={`ai-task-assignee-${task.id}`}
            style={{
              padding: '7px 9px', fontFamily: 'Lexend', fontSize: 12,
              background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 8, color: 'var(--t1)',
            }}
          >
            <option value="">— Non assigné —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <button
              type="button"
              data-testid={`ai-task-save-${task.id}`}
              onClick={save}
              className="asa-btn"
              style={{ flex: 1, fontSize: 11, padding: '6px 10px' }}
            >Enregistrer</button>
            <button
              type="button"
              onClick={() => { setTitle(task.title); setDue(task.due_date || ''); setStartT(task.start_time || ''); setEditing(false); }}
              className="asa-btn-ghost asa-btn"
              style={{ fontSize: 11, padding: '6px 10px' }}
            >Annuler</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)' }}>
              {task.title}
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap',
              fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)',
            }}>
              {task.due_date && <span>📅 {task.due_date}</span>}
              {task.start_time && <span>🕒 {task.start_time}{task.end_time ? `–${task.end_time}` : ''}</span>}
              <span>· {task.event_type}</span>
              <span>· {task.priority}</span>
              {task.assigned_to && (
                <span>· 👤 {(users.find((u) => u.id === task.assigned_to) || {}).name || task.assigned_to}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            data-testid={`ai-task-edit-${task.id}`}
            onClick={() => setEditing(true)}
            style={{
              padding: 6, borderRadius: 7,
              background: 'var(--sf)', border: '1.5px solid var(--bd)',
            }}
            title="Modifier"
          >
            <ArrowUpRight size={11} color="var(--t3)" />
          </button>
          <button
            type="button"
            data-testid={`ai-task-delete-${task.id}`}
            onClick={onDelete}
            style={{
              padding: 6, borderRadius: 7,
              background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)',
            }}
            title="Supprimer"
          >
            <Trash2 size={11} color="var(--r)" />
          </button>
        </div>
      )}
    </div>
  );
}


